import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Briefcase, Plus, Search, Calendar, DollarSign, Package,
  MapPin, Home, Loader2, RefreshCw, Edit, Phone, Mail, User, Trash2,
  Eye, Clock, CheckCircle, Route, Image, X, Download, Link2, Copy, Archive,
  FileSpreadsheet, Upload, AlertTriangle, Copy as CopyIcon, Grid3X3, Send,
  ChevronLeft, ChevronRight, ArrowUpDown, CheckSquare, Users, XCircle, EyeOff, UserX, UserPlus, Settings
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../../components/ui/dialog';
import { MapContainer, TileLayer, Polygon, Polyline, Marker, Circle, useMapEvents, useMap, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import GoogleMapDisplay from '../../components/map/GoogleMapDisplay';
import LazyGoogleMap from '../../components/map/LazyGoogleMap';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fix for default marker icons in Leaflet with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom house marker icon for delivery zone
const houseMarkerIcon = new L.DivIcon({
  className: 'house-marker',
  html: `<div style="
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;
    border-radius: 3px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.3);
  ">
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
      <polyline points="9 22 9 12 15 12 15 22"></polyline>
    </svg>
  </div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

// Grid label marker icon
const createGridLabelIcon = (label) => new L.DivIcon({
  className: 'grid-label-marker',
  html: `<div style="
    background: rgba(16, 185, 129, 0.7);
    color: white;
    font-weight: bold;
    font-size: 11px;
    padding: 2px 6px;
    border-radius: 4px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
    white-space: nowrap;
    text-align: center;
    min-width: 20px;
  ">${label}</div>`,
  iconSize: [30, 18],
  iconAnchor: [15, 9],
});
const API = `${BACKEND_URL}/api`;

// Boundary point marker icon
const boundaryPointIcon = new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="
    width: 16px;
    height: 16px;
    background: #8b5cf6;
    border: 3px solid white;
    border-radius: 50%;
    box-shadow: 0 2px 6px rgba(139, 92, 246, 0.5);
    cursor: grab;
  "></div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

// Draggable marker component
const DraggableMarker = ({ position, index, onDragEnd }) => {
  const markerRef = React.useRef(null);
  
  const eventHandlers = React.useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;
        if (marker != null) {
          const newPos = marker.getLatLng();
          onDragEnd(index, [newPos.lat, newPos.lng]);
        }
      },
    }),
    [index, onDragEnd],
  );

  return (
    <Marker
      draggable={true}
      eventHandlers={eventHandlers}
      position={position}
      ref={markerRef}
      icon={boundaryPointIcon}
    />
  );
};

// Clickable edge segment component - allows adding new points by clicking on edges
const ClickableEdge = ({ start, end, insertIndex, onInsertPoint }) => {
  return (
    <Polyline
      positions={[start, end]}
      pathOptions={{
        color: 'transparent',
        weight: 20, // Wide clickable area
        opacity: 0,
      }}
      eventHandlers={{
        click: (e) => {
          L.DomEvent.stopPropagation(e);
          const clickedPoint = [e.latlng.lat, e.latlng.lng];
          onInsertPoint(insertIndex, clickedPoint);
        },
        mouseover: (e) => {
          e.target.setStyle({ color: '#8b5cf6', opacity: 0.5, weight: 8 });
        },
        mouseout: (e) => {
          e.target.setStyle({ color: 'transparent', opacity: 0, weight: 20 });
        }
      }}
    />
  );
};

// Map click handler component
const MapClickHandler = ({ onMapClick, onCircleClick, isDrawing, isCircleMode }) => {
  useMapEvents({
    click: (e) => {
      if (isDrawing) {
        if (isCircleMode) {
          onCircleClick([e.latlng.lat, e.latlng.lng]);
        } else {
          onMapClick([e.latlng.lat, e.latlng.lng]);
        }
      }
    },
  });
  return null;
};

// Map bounds fitter for polygon
const MapBoundsFitter = ({ points }) => {
  const map = useMap();
  
  useEffect(() => {
    if (points.length >= 3) {
      const bounds = L.latLngBounds(points);
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [points, map]);
  
  return null;
};

// Map bounds updater for address points
const MapBoundsUpdater = ({ points }) => {
  const map = useMap();
  
  useEffect(() => {
    if (points.length > 0) {
      const bounds = L.latLngBounds(points);
      map.fitBounds(bounds, { padding: [30, 30] });
    }
  }, [points, map]);
  
  return null;
};

// Map bounds fitter for circle
const CircleBoundsFitter = ({ center, radiusMiles }) => {
  const map = useMap();
  
  useEffect(() => {
    if (center) {
      const radiusMeters = radiusMiles * 1609.34;
      const bounds = L.latLng(center).toBounds(radiusMeters * 2);
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [center, radiusMiles, map]);
  
  return null;
};

// Map center updater for address search - moves map to new center when address is selected
const MapCenterUpdater = ({ center, zoom = 16 }) => {
  const map = useMap();
  
  useEffect(() => {
    if (center && center[0] && center[1]) {
      map.setView(center, zoom, { animate: true, duration: 0.5 });
    }
  }, [center, zoom, map]);
  
  return null;
};

// Center point marker for circle zones (draggable)
const centerPointIcon = new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="
    width: 20px;
    height: 20px;
    background: #f97316;
    border: 3px solid white;
    border-radius: 50%;
    box-shadow: 0 2px 6px rgba(249, 115, 22, 0.5);
    cursor: grab;
  "></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

const AdminJobs = () => {
  const navigate = useNavigate();
  const { getAuthHeader, userType } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  // Lightweight {user_id → {name, email}} lookup populated once on mount.
  // Drives the bold worker-name row + click-to-message dialog on every
  // job card without re-fetching per-card. Auditors get this too — they
  // can message workers even though they can't edit jobs.
  const [userMap, setUserMap] = useState({});
  // Message dialog state — opens when an admin clicks a worker name
  // on a job card. Body is sent via the existing support-chat reply
  // endpoint so the conversation lands in the user's Messages inbox
  // exactly the same way as Support replies.
  const [messageWorker, setMessageWorker] = useState(null); // {user_id, name, job_title}
  const [messageBody, setMessageBody] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [drawingMode, setDrawingMode] = useState(false);
  const [mapCenter, setMapCenter] = useState([40.7128, -74.006]);
  
  // Check if current user is full admin (can see client payment)
  const isFullAdmin = userType === 'admin';
  const isAuditor = userType === 'auditor';
  const [applyingRate, setApplyingRate] = useState(false);
  // Managers can also form/manage teams on in-progress jobs AND split
  // parent jobs into grid sections. Backend `admin_create_or_update_team`
  // / remove / disband / `create_job` / `mark_job_as_split` all already
  // accept managers via `require_manager_or_admin` — this gate just
  // surfaces the UI button for them.
  const canManageTeams = userType === 'admin' || userType === 'manager';
  const canSplitJobs = userType === 'admin' || userType === 'manager';
  // Generic "manager-or-admin" gate for actions whose backend
  // endpoints use `require_manager_or_admin` (assign-user, remove-user,
  // mark-complete, toggle-hidden, refresh-house-cache, repopulate-
  // addresses, clone job). Edit/delete/unarchive remain admin-only
  // because the backend enforces that role boundary explicitly.
  const canManageJobs = userType === 'admin' || userType === 'manager';
  const [boundaryPoints, setBoundaryPoints] = useState([]);
  const [houseCount, setHouseCount] = useState(null);
  const [addressBreakdown, setAddressBreakdown] = useState(null);
  const [gridSections, setGridSections] = useState([]);
  const [countingHouses, setCountingHouses] = useState(false);
  const [countConfidence, setCountConfidence] = useState(null); // 'high', 'medium', 'low', 'estimated'
  const [creatingJobs, setCreatingJobs] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [hideGridsForPDF, setHideGridsForPDF] = useState(false);
  const [geocodingAddresses, setGeocodingAddresses] = useState(false);
  
  // Ref for map container (for PDF generation)
  const mapContainerRef = useRef(null);
  
  // Job Detail View state
  const [showJobDetail, setShowJobDetail] = useState(false);
  const [selectedJobDetail, setSelectedJobDetail] = useState(null);
  const [jobDeliveryHistory, setJobDeliveryHistory] = useState(null);
  const [loadingJobDetail, setLoadingJobDetail] = useState(false);
  const [jobHouseLocations, setJobHouseLocations] = useState([]);
  const [enlargedPhoto, setEnlargedPhoto] = useState(null);
  const [childProgress, setChildProgress] = useState(null);
  const [teamInfo, setTeamInfo] = useState(null);
  
  // Team management modal state
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [teamModalJob, setTeamModalJob] = useState(null);
  const [teamModalData, setTeamModalData] = useState(null);
  const [teamModalLoading, setTeamModalLoading] = useState(false);
  const [teamUserSearch, setTeamUserSearch] = useState('');
  const [teamAvailableUsers, setTeamAvailableUsers] = useState([]);
  const [teamSelectedUsers, setTeamSelectedUsers] = useState([]);
  
  // Bulk selection state
  const [selectedJobs, setSelectedJobs] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkCompleting, setBulkCompleting] = useState(false);
  
  // Split into Grid Jobs state
  const [splittingIntoGrids, setSplittingIntoGrids] = useState(false);
  // When set, opens the "Custom split count" modal for this job
  const [splitModalJob, setSplitModalJob] = useState(null);
  const [customSplitCount, setCustomSplitCount] = useState(4);

  // Returns true when a job is eligible to be split into grid sections.
  // Allows in_progress jobs too — when an already-started job is split, the
  // deliveries that have already been completed are transferred onto the
  // matching child grid (see handleSplitIntoGridJobs → seed-progress flow).
  const _canSplit = (job) => {
    if (!job) return false;
    if (job.status !== 'open' && job.status !== 'pending_approval' && job.status !== 'in_progress') return false;
    if (job.is_split_parent) return false;
    const hasBoundary = Array.isArray(job.delivery_boundary) && job.delivery_boundary.length >= 3;
    const isAddrType = job.delivery_type === 'address_specific' || job.delivery_type === 'canvassing';
    const geocodedCount = isAddrType
      ? (job.specific_addresses || []).filter((a) => a.lat && a.lng).length
      : 0;
    return hasBoundary || (isAddrType && geocodedCount >= 2);
  };
  
  // Send client approval email state
  const [sendingApprovalEmail, setSendingApprovalEmail] = useState({});

  // Notify Silently — per-job checkbox; notifying actions (Assign User,
  // Remove User, Mark Complete) skip worker/customer notifications when checked.
  const [silentJobIds, setSilentJobIds] = useState(new Set());
  const toggleJobSilent = (jobId) => {
    setSilentJobIds(prev => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId); else next.add(jobId);
      return next;
    });
  };
  
  // Pending job approval state
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [jobToApprove, setJobToApprove] = useState(null);
  const [approvalBonusPay, setApprovalBonusPay] = useState(0);
  const [approvingJob, setApprovingJob] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const JOBS_PER_PAGE = 15;
  
  // Sort state
  const [sortBy, setSortBy] = useState('default');
  const [showSortMenu, setShowSortMenu] = useState(false);
  
  // Status filter state
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Edit progress state
  const [showEditProgressModal, setShowEditProgressModal] = useState(false);
  const [editProgressData, setEditProgressData] = useState({
    deliveries_count: 0,
    distance: 0,
    notes: ''
  });
  const [savingProgress, setSavingProgress] = useState(false);
  const [progressDetails, setProgressDetails] = useState(null);
  
  // Remove user from job state
  const [showRemoveUserModal, setShowRemoveUserModal] = useState(false);
  const [removeUserJob, setRemoveUserJob] = useState(null);
  const [removeUserPostStatus, setRemoveUserPostStatus] = useState('open');
  const [removingUser, setRemovingUser] = useState(false);
  
  // Assign user to job state
  const [showAssignUserModal, setShowAssignUserModal] = useState(false);
  const [assignUserJob, setAssignUserJob] = useState(null);
  const [assignUserList, setAssignUserList] = useState([]);
  const [assignUserSearch, setAssignUserSearch] = useState('');
  const [assignSelectedUserId, setAssignSelectedUserId] = useState('');
  const [assigningUser, setAssigningUser] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  const DEFAULT_RADIUS_MILES = 1;
  const APARTMENT_CIRCLE_RADIUS_MILES = 0.05;
  const MIN_JOB_VALUE = 100; // Minimum $100 per job/grid
  
  // Fixed at 500 houses per grid - leftover houses in final grid are acceptable
  const calculateMaxPerGrid = (payPerFlyer) => {
    return 500;
  };
  
  const MAX_APARTMENTS_PER_GRID = 1000;
  
  // Delivery type configurations
  const DELIVERY_TYPES = [
    { value: 'houses', label: 'Houses', icon: '🏠', zoneType: 'polygon', needsGrid: true, autoCount: true },
    { value: 'apartments', label: 'Apartments', icon: '🏢', zoneType: 'polygon', needsGrid: true, autoCount: true },
    { value: 'address_specific', label: 'Address Specific', icon: '📍', zoneType: 'points', needsGrid: false, autoCount: false },
    { value: 'cars', label: 'Cars', icon: '🚗', zoneType: 'circle', needsGrid: false, autoCount: false },
    { value: 'hands', label: 'Hands (Direct)', icon: '🤝', zoneType: 'circle', needsGrid: false, autoCount: false },
  ];
  
  // Circle zone state for Cars/Hands
  const [circleCenter, setCircleCenter] = useState(null);
  const [circleRadius, setCircleRadius] = useState(DEFAULT_RADIUS_MILES); // in miles
  
  // Address search state
  const [addressSearchQuery, setAddressSearchQuery] = useState('');
  const [searchingAddress, setSearchingAddress] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const addressSearchRef = useRef(null);
  const [mapCenterTrigger, setMapCenterTrigger] = useState(0); // Trigger for map re-centering
  
  // Address specific delivery state
  const [addressFile, setAddressFile] = useState(null);
  const [parsedAddresses, setParsedAddresses] = useState([]);
  const [parsingFile, setParsingFile] = useState(false);
  
  // Apartment complexes found within polygon
  const [apartmentComplexes, setApartmentComplexes] = useState([]);
  
  const [newJob, setNewJob] = useState({
    title: '',
    client: '',
    client_contact_name: '',
    client_phone: '',
    client_email: '',
    location: '',
    delivery_type: 'houses',
    deliveries: 100,
    pay_per_flyer: 0.25,
    client_payment: 0,
    bonus_pay: 0,
    deadline: '',
    flyer_type: '',
    description: '',
    image_url: ''
  });
  
  // Get current delivery type config
  const currentDeliveryType = DELIVERY_TYPES.find(t => t.value === newJob.delivery_type) || DELIVERY_TYPES[0];
  const usesPolygon = currentDeliveryType.zoneType === 'polygon';
  const usesCircle = currentDeliveryType.zoneType === 'circle';
  const needsGrid = currentDeliveryType.needsGrid;
  const autoCount = currentDeliveryType.autoCount;

  useEffect(() => {
    fetchJobs();
    fetchUserMap();
  }, []);

  // Pulls every approved user once for the user-id → name map. Cheap —
  // ~tens-of-KB payload, and the admin already calls this elsewhere
  // (assign-user / form-team modals). Failure is non-fatal: the worker
  // row just falls back to the raw id if a lookup misses.
  const fetchUserMap = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, getAuthHeader());
      const map = {};
      (res.data || []).forEach(u => {
        if (u && u.id) map[u.id] = { name: u.name || u.email || u.id, email: u.email || '' };
      });
      setUserMap(map);
    } catch (e) {
      // Swallow — worker chips will still render with raw ids, no crash.
      console.warn('Failed to fetch user map for AdminJobs worker chips', e);
    }
  };

  // Click-to-message a worker straight from the job card. Posts via the
  // same `/admin/support/conversations/{user_id}/reply` endpoint used
  // by the dedicated Support page so the user sees this message in
  // their support inbox without a separate flow.
  const handleSendWorkerMessage = async () => {
    if (!messageWorker || !messageBody.trim()) {
      toast.error('Please enter a message');
      return;
    }
    setSendingMessage(true);
    try {
      await axios.post(
        `${API}/admin/support/conversations/${messageWorker.user_id}/reply`,
        { message: messageBody.trim() },
        getAuthHeader(),
      );
      toast.success(`Message sent to ${messageWorker.name}`);
      setMessageWorker(null);
      setMessageBody('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/jobs`, getAuthHeader());
      setJobs(response.data);
    } catch (error) {
      toast.error('Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  };

  // Polygon map click handler
  const handleMapClick = (point) => {
    setBoundaryPoints(prev => [...prev, point]);
    setHouseCount(null);
    setAddressBreakdown(null);
    setGridSections([]);
  };

  // Circle center click handler
  const handleCircleClick = (point) => {
    setCircleCenter(point);
    toast.success('Circle center set. Adjust radius below.');
  };

  // Circle center drag handler
  const handleCircleCenterDrag = (newPosition) => {
    setCircleCenter(newPosition);
  };

  const handleMarkerDragEnd = (index, newPosition) => {
    setBoundaryPoints(prev => {
      const updated = [...prev];
      updated[index] = newPosition;
      return updated;
    });
    setHouseCount(null);
    setAddressBreakdown(null);
    setGridSections([]);
  };

  // Insert a new point on a boundary edge
  const handleInsertPoint = (insertIndex, newPoint) => {
    setBoundaryPoints(prev => {
      const updated = [...prev];
      updated.splice(insertIndex, 0, newPoint);
      return updated;
    });
    setHouseCount(null);
    setAddressBreakdown(null);
    setGridSections([]);
    toast.success('Point added to boundary');
  };

  const handleUndoPoint = () => {
    setBoundaryPoints(prev => prev.slice(0, -1));
    setHouseCount(null);
    setAddressBreakdown(null);
    setGridSections([]);
  };

  const handleClearPoints = () => {
    setBoundaryPoints([]);
    setHouseCount(null);
  };

  // Handle address file upload for address_specific delivery type
  const handleAddressFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setParsingFile(true);
    setAddressFile(file);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/parse-address-file`,
        formData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.success) {
        setParsedAddresses(response.data.addresses);
        
        // Auto-update delivery count
        setNewJob(prev => ({
          ...prev,
          deliveries: response.data.total_addresses
        }));
        
        // Calculate center for map
        const coordAddresses = response.data.addresses.filter(a => a.lat && a.lng);
        if (coordAddresses.length > 0) {
          const avgLat = coordAddresses.reduce((sum, a) => sum + a.lat, 0) / coordAddresses.length;
          const avgLng = coordAddresses.reduce((sum, a) => sum + a.lng, 0) / coordAddresses.length;
          setMapCenter([avgLat, avgLng]);
        }
        
        toast.success(`Loaded ${response.data.total_addresses} addresses from file`);
        
        if (!response.data.has_coordinates) {
          toast.info('Addresses will need geocoding before delivery tracking', {
            duration: 5000
          });
        }
      }
    } catch (error) {
      console.error('Error parsing address file:', error);
      toast.error(error.response?.data?.detail || 'Failed to parse address file');
      setAddressFile(null);
      setParsedAddresses([]);
    } finally {
      setParsingFile(false);
    }
  };

  // Search for address using OpenStreetMap Nominatim API
  const handleAddressSearch = async (query, autoSelect = false) => {
    if (!query || query.length < 3) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      return;
    }

    setSearchingAddress(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&addressdetails=1`,
        {
          headers: {
            'Accept': 'application/json',
            'User-Agent': 'LocalFlyerDelivery/1.0'
          }
        }
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        const suggestions = data.map(item => ({
          display_name: item.display_name,
          lat: parseFloat(item.lat),
          lon: parseFloat(item.lon),
          type: item.type,
          address: item.address
        }));
        
        setAddressSuggestions(suggestions);
        
        // If autoSelect is true (from button click), automatically select the first result
        if (autoSelect && suggestions.length > 0) {
          handleSelectAddress(suggestions[0]);
        } else {
          setShowAddressSuggestions(true);
        }
      } else {
        setAddressSuggestions([]);
        setShowAddressSuggestions(false);
        toast.error('No results found for this address');
      }
    } catch (error) {
      console.error('Error searching address:', error);
      toast.error('Failed to search address');
    } finally {
      setSearchingAddress(false);
    }
  };

  // Select an address suggestion and move map
  const handleSelectAddress = (suggestion) => {
    setMapCenter([suggestion.lat, suggestion.lon]);
    setMapCenterTrigger(prev => prev + 1); // Trigger map re-centering
    setAddressSearchQuery(suggestion.display_name.split(',')[0]); // Just the main part
    setShowAddressSuggestions(false);
    
    // Also update the job location field
    const shortAddress = suggestion.display_name.split(',').slice(0, 3).join(',');
    setNewJob(prev => ({ ...prev, location: shortAddress }));
    
    toast.success('Map moved to selected location');
  };

  // Debounce address search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (addressSearchQuery.length >= 3) {
        handleAddressSearch(addressSearchQuery);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [addressSearchQuery]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (addressSearchRef.current && !addressSearchRef.current.contains(event.target)) {
        setShowAddressSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const calculateCenter = (points) => {
    if (points.length === 0) return [40.7128, -74.0060];
    const lat = points.reduce((sum, p) => sum + p[0], 0) / points.length;
    const lng = points.reduce((sum, p) => sum + p[1], 0) / points.length;
    return [lat, lng];
  };

  // Count addresses within the boundary using Overpass API - filtered by delivery type
  const countAddressesInBoundary = useCallback(async () => {
    if (boundaryPoints.length < 3) {
      toast.error('Please draw at least 3 points first');
      return;
    }

    setCountingHouses(true);
    setAddressBreakdown(null);
    setGridSections([]);
    
    const deliveryType = newJob.delivery_type;
    
    try {
      // Convert points to Overpass polygon format (lat lon pairs)
      const polygonCoords = boundaryPoints
        .map(p => `${p[0]} ${p[1]}`)
        .join(' ');
      const closedPolygon = `${polygonCoords} ${boundaryPoints[0][0]} ${boundaryPoints[0][1]}`;
      
      // Build query based on delivery type
      let query;
      if (deliveryType === 'apartments') {
        // Query specifically for apartment buildings
        query = `
          [out:json][timeout:60];
          (
            way["building"="apartments"](poly:"${closedPolygon}");
            way["building"="apartment"](poly:"${closedPolygon}");
            way["building"="residential"]["building:levels"~"^[3-9]|[1-9][0-9]"](poly:"${closedPolygon}");
            way["building"="flats"](poly:"${closedPolygon}");
            relation["building"="apartments"](poly:"${closedPolygon}");
          );
          out body center;
        `;
      } else {
        // Houses - query for RESIDENTIAL buildings only (exclude commercial, industrial, water features)
        query = `
          [out:json][timeout:60];
          (
            way["building"="house"](poly:"${closedPolygon}");
            way["building"="residential"](poly:"${closedPolygon}");
            way["building"="detached"](poly:"${closedPolygon}");
            way["building"="semidetached_house"](poly:"${closedPolygon}");
            way["building"="terrace"](poly:"${closedPolygon}");
            way["building"="yes"]["addr:housenumber"](poly:"${closedPolygon}");
            node["addr:housenumber"](poly:"${closedPolygon}");
          );
          out body center;
        `;
      }
      
      const response = await axios.post(
        'https://overpass-api.de/api/interpreter',
        `data=${encodeURIComponent(query)}`,
        {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          timeout: 60000
        }
      );
      
      const elements = response.data.elements || [];
      let residential = 0;
      let apartmentUnits = 0;
      let business = 0;
      const allLocations = [];
      const foundApartments = [];
      
      if (deliveryType === 'apartments') {
        // For apartments, count units per building and store locations
        elements.forEach(el => {
          const lat = el.lat || el.center?.lat;
          const lng = el.lon || el.center?.lon;
          const buildingType = el.tags?.building || '';
          
          // Only process actual apartment buildings
          const isApartment = ['apartments', 'apartment', 'flats', 'residential'].includes(buildingType);
          const levels = parseInt(el.tags?.['building:levels']) || 0;
          
          // Skip if not an apartment type OR if residential with less than 3 levels
          if (!isApartment || (buildingType === 'residential' && levels < 3)) {
            return;
          }
          
          if (lat && lng) {
            // Estimate units based on building levels (default 8 units per floor)
            const actualLevels = levels || 3;
            const unitsPerFloor = parseInt(el.tags?.['building:flats']) || 8;
            const estimatedUnits = actualLevels * unitsPerFloor;
            
            // Get address info - prioritize full address over name
            const houseNum = el.tags?.['addr:housenumber'] || '';
            const street = el.tags?.['addr:street'] || '';
            const city = el.tags?.['addr:city'] || '';
            const state = el.tags?.['addr:state'] || '';
            const postcode = el.tags?.['addr:postcode'] || '';
            const name = el.tags?.name || '';
            
            // Build full address
            let fullAddress = '';
            if (street) {
              fullAddress = `${houseNum} ${street}`.trim();
              if (city) fullAddress += `, ${city}`;
              if (state) fullAddress += `, ${state}`;
              if (postcode) fullAddress += ` ${postcode}`;
            } else if (name) {
              fullAddress = name;
            }
            
            // Final fallback - use building number
            const address = fullAddress || `Apartment Building #${foundApartments.length + 1}`;
            
            apartmentUnits += estimatedUnits;
            allLocations.push({ lat, lng, type: 'apartment', units: estimatedUnits });
            foundApartments.push({ 
              lat, 
              lng, 
              units: estimatedUnits, 
              address,
              name: name || `Building #${foundApartments.length + 1}`
            });
          }
        });
        
        // If no apartments found via API, provide a helpful message
        if (foundApartments.length === 0) {
          toast.info('No apartment buildings found in this area via map data. You can manually enter the delivery count.');
          setApartmentComplexes([]);
          setHouseCount(0);
          setAddressBreakdown({ residential: 0, apartments: 0, business: 0, total: 0 });
          setGridSections([]);
          return;
        }
        
        console.log('Found apartments:', foundApartments);
        setApartmentComplexes(foundApartments);
        setHouseCount(apartmentUnits);
        setAddressBreakdown({ residential: 0, apartments: apartmentUnits, business: 0, total: apartmentUnits });
        
        // Build description with apartment details
        const descriptionText = `Apartment Complexes (${foundApartments.length} buildings):\n\n${foundApartments.map((a, i) => 
          `${i+1}. ${a.name !== 'Apartment Complex' ? a.name + ' - ' : ''}${a.address}\n   Estimated Units: ~${a.units}`
        ).join('\n\n')}`;
        
        setNewJob(prev => ({ 
          ...prev, 
          deliveries: apartmentUnits,
          description: descriptionText
        }));
        
        // Calculate grid sections for apartments (1000 units per grid)
        const apartmentLocations = foundApartments.map(a => ({ lat: a.lat, lng: a.lng, type: 'apartment' }));
        const grids = calculateGridSections(boundaryPoints, apartmentUnits, apartmentLocations, MAX_APARTMENTS_PER_GRID);
        setGridSections(grids);
        
        toast.success(`Found ${foundApartments.length} apartment buildings with ~${apartmentUnits} units (${grids.length} job section${grids.length > 1 ? 's' : ''})`);
        
      } else {
        // Houses - filter to ONLY residential houses, exclude all commercial
        setApartmentComplexes([]);
        
        // Expanded exclude list for building types
        const excludeBuildingTypes = [
          'apartments', 'apartment', 'flats', 'dormitory',
          'commercial', 'retail', 'office', 'industrial', 'warehouse', 
          'shop', 'supermarket', 'store', 'mall', 'kiosk',
          'garage', 'garages', 'carport', 'parking',
          'shed', 'barn', 'farm', 'farm_auxiliary',
          'church', 'chapel', 'mosque', 'temple', 'synagogue', 'religious',
          'school', 'university', 'college', 'kindergarten',
          'hospital', 'clinic', 'doctors',
          'stadium', 'sports_centre', 'sports_hall', 'pavilion',
          'bridge', 'roof', 'ruins', 'construction', 'greenhouse', 
          'hut', 'cabin', 'service', 'transformer_tower', 'water_tower'
        ];
        
        // Tags that indicate commercial use
        const commercialTags = ['shop', 'amenity', 'office', 'tourism', 'leisure'];
        
        // Words in names that indicate commercial
        const commercialNameWords = ['center', 'centre', 'plaza', 'mall', 'market', 'store', 'shop', 
                                      'restaurant', 'cafe', 'bank', 'hotel', 'motel', 'inn', 
                                      'office', 'business', 'commercial', 'retail', 'village'];
        
        elements.forEach(el => {
          const tags = el.tags || {};
          const buildingType = tags.building || '';
          const name = (tags.name || '').toLowerCase();
          const lat = el.lat || el.center?.lat;
          const lng = el.lon || el.center?.lon;
          
          if (!lat || !lng) return;
          
          // Skip excluded building types
          if (excludeBuildingTypes.includes(buildingType)) {
            if (['commercial', 'retail', 'office', 'industrial', 'warehouse', 'shop', 'supermarket', 'store'].includes(buildingType)) {
              business++;
            }
            return;
          }
          
          // Skip if has commercial tags
          for (const tag of commercialTags) {
            if (tags[tag]) {
              business++;
              return;
            }
          }
          
          // Skip if name contains commercial words
          for (const word of commercialNameWords) {
            if (name.includes(word)) {
              business++;
              return;
            }
          }
          
          // Skip if has brand or operator (indicates business)
          if (tags.brand || tags.operator) {
            business++;
            return;
          }
          
          // Count as residential house
          residential++;
          allLocations.push({ lat, lng, type: 'residential' });
        });
        
        // If count is low, estimate based on area
        let finalCount = residential;
        if (residential < 50) {
          const areaEstimate = estimateHousesFromArea(boundaryPoints);
          if (areaEstimate > residential) {
            finalCount = Math.round(areaEstimate * 0.8); // 80% residential
            residential = finalCount;
            setCountConfidence('low'); // API returned few results, used area estimate
          }
        } else {
          // Good data from API
          setCountConfidence('high');
        }
        
        setHouseCount(finalCount);
        setAddressBreakdown({ residential: finalCount, apartments: 0, business, total: finalCount });
        setNewJob(prev => ({ ...prev, deliveries: finalCount }));
        
        // Calculate grid sections for houses - ensure minimum $100 job value per grid
        const maxPerGrid = calculateMaxPerGrid(newJob.pay_per_flyer);
        const grids = calculateGridSections(boundaryPoints, finalCount, allLocations, maxPerGrid);
        setGridSections(grids);
        
        const jobValue = grids.length > 0 ? (finalCount / grids.length) * newJob.pay_per_flyer : 0;
        toast.success(`Found ${finalCount} houses (${grids.length} job section${grids.length > 1 ? 's' : ''}, ~$${jobValue.toFixed(0)}/job)`);
      }
      
    } catch (error) {
      console.error('Error counting addresses:', error);
      // Fallback to area-based estimation
      const areaEstimate = estimateHousesFromArea(boundaryPoints);
      setCountConfidence('estimated'); // Fallback estimation
      
      if (deliveryType === 'apartments') {
        const apartmentEstimate = Math.round(areaEstimate * 0.3 * 24); // 30% of area, 24 units avg
        setHouseCount(apartmentEstimate);
        setAddressBreakdown({ residential: 0, apartments: apartmentEstimate, business: 0, total: apartmentEstimate });
        setNewJob(prev => ({ ...prev, deliveries: apartmentEstimate }));
        const grids = calculateGridSections(boundaryPoints, apartmentEstimate, [], MAX_APARTMENTS_PER_GRID);
        setGridSections(grids);
        toast.warning(`Estimated ${apartmentEstimate} apartment units (${grids.length} section${grids.length > 1 ? 's' : ''}) - Please verify manually`);
      } else {
        const houseEstimate = Math.round(areaEstimate * 0.8);
        setHouseCount(houseEstimate);
        setAddressBreakdown({ residential: houseEstimate, apartments: 0, business: 0, total: houseEstimate });
        setNewJob(prev => ({ ...prev, deliveries: houseEstimate }));
        const maxPerGrid = calculateMaxPerGrid(newJob.pay_per_flyer);
        const grids = calculateGridSections(boundaryPoints, houseEstimate, [], maxPerGrid);
        setGridSections(grids);
        toast.warning(`Estimated ${houseEstimate} houses (${grids.length} section${grids.length > 1 ? 's' : ''}) - Please verify manually`);
      }
    } finally {
      setCountingHouses(false);
    }
  }, [boundaryPoints, newJob.delivery_type, newJob.pay_per_flyer]);

  // Generate row letter (A, B, C... Z, AA, AB, etc.)
  const getRowLetter = (rowIndex) => {
    let label = '';
    let num = rowIndex;
    while (num >= 0) {
      label = String.fromCharCode(65 + (num % 26)) + label;
      num = Math.floor(num / 26) - 1;
    }
    return label;
  };

  // Calculate grid sections for the boundary (configurable max per section)
  // Grids are fixed at 500 houses; standard worker rate is $0.25/delivery
  const calculateGridSections = (boundary, totalCount, locations, maxPerGrid = 500) => {
    if (boundary.length < 3 || totalCount === 0) return [];
    
    // Target 400-500 houses per grid
    const TARGET_MIN = 400;
    const TARGET_MAX = 500;
    const targetPerGrid = 450; // aim for middle of range
    
    const numSections = Math.ceil(totalCount / targetPerGrid);
    if (numSections <= 1 || totalCount <= 500) {
      return [{ boundary, houseCount: totalCount, index: 1, label: 'A1', center: calculateCenter(boundary) }];
    }
    
    // Calculate bounding box
    const lats = boundary.map(p => p[0]);
    const lngs = boundary.map(p => p[1]);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    
    // Determine grid dimensions - keep rows × cols as close to numSections as possible
    const aspectRatio = (maxLng - minLng) / (maxLat - minLat) || 1;
    let cols, rows;
    
    // Find rows × cols combination closest to numSections without going too far over
    let bestRows = 1, bestCols = numSections, bestDiff = Infinity;
    for (let r = 1; r <= numSections; r++) {
      const c = Math.ceil(numSections / r);
      const total = r * c;
      const actualRatio = c / r;
      const ratioDiff = Math.abs(actualRatio - aspectRatio);
      // Prefer combinations where total is close to numSections and aspect matches
      const diff = (total - numSections) * 2 + ratioDiff;
      if (diff < bestDiff) {
        bestDiff = diff;
        bestRows = r;
        bestCols = c;
      }
    }
    rows = bestRows;
    cols = bestCols;
    
    const latStep = (maxLat - minLat) / rows;
    const lngStep = (maxLng - minLng) / cols;
    
    const grids = [];
    let gridIndex = 0;
    
    // Iterate from top (highest lat) to bottom, left to right
    for (let r = rows - 1; r >= 0; r--) {
      const rowLetter = getRowLetter(rows - 1 - r);
      let colNumber = 1;
      
      for (let c = 0; c < cols; c++) {
        const gridMinLat = minLat + r * latStep;
        const gridMaxLat = minLat + (r + 1) * latStep;
        const gridMinLng = minLng + c * lngStep;
        const gridMaxLng = minLng + (c + 1) * lngStep;
        
        const gridBoundary = [
          [gridMinLat, gridMinLng],
          [gridMinLat, gridMaxLng],
          [gridMaxLat, gridMaxLng],
          [gridMaxLat, gridMinLng],
        ];
        
        // Check if this grid intersects with the original boundary
        const gridCenter = [(gridMinLat + gridMaxLat) / 2, (gridMinLng + gridMaxLng) / 2];
        if (isPointInPolygon(gridCenter, boundary)) {
          // If actual locations available, count houses in this cell
          let cellCount = 0;
          if (locations && locations.length > 0) {
            cellCount = locations.filter(h => {
              const lat = h.lat || h[0];
              const lng = h.lon || h.lng || h[1];
              return lat >= gridMinLat && lat <= gridMaxLat && lng >= gridMinLng && lng <= gridMaxLng;
            }).length;
          }
          
          const label = `${rowLetter}${colNumber}`;
          grids.push({
            boundary: gridBoundary,
            houseCount: cellCount > 0 ? cellCount : Math.ceil(totalCount / (rows * cols)),
            index: gridIndex + 1,
            label: label,
            center: gridCenter,
            hasActualCount: cellCount > 0
          });
          gridIndex++;
          colNumber++;
        }
      }
    }
    
    // Remove empty grids if we have actual location data
    if (locations && locations.length > 0) {
      return grids.filter(g => g.houseCount > 0);
    }
    
    return grids;
  };

  // Point-in-polygon check
  const isPointInPolygon = (point, polygon) => {
    const [lat, lng] = point;
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i][0], yi = polygon[i][1];
      const xj = polygon[j][0], yj = polygon[j][1];
      if ((yi > lng) !== (yj > lng) && lat < (xj - xi) * (lng - yi) / (yj - yi) + xi) {
        inside = !inside;
      }
    }
    return inside;
  };

  // Estimate houses based on polygon area (fallback method)
  const estimateHousesFromArea = (points) => {
    if (points.length < 3) return 0;
    
    // Calculate polygon area using Shoelace formula
    let area = 0;
    const n = points.length;
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      area += points[i][1] * points[j][0];
      area -= points[j][1] * points[i][0];
    }
    area = Math.abs(area) / 2;
    
    // Convert to approximate square meters
    const areaInSqMeters = area * 111000 * 85000;
    
    // Estimate 1 house per 700 sq meters in residential areas
    const estimatedHouses = Math.round(areaInSqMeters / 700);
    
    return Math.max(estimatedHouses, 1);
  };

  const handleCreateJob = async (e) => {
    e.preventDefault();
    
    // Validate zone based on delivery type
    if (usesPolygon && boundaryPoints.length < 3) {
      toast.error('Please draw at least 3 points for the delivery zone boundary');
      return;
    }
    if (usesCircle && !circleCenter) {
      toast.error('Please click on the map to set the delivery zone center');
      return;
    }
    if (newJob.delivery_type === 'address_specific' && parsedAddresses.length === 0) {
      toast.error('Please upload an address file first');
      return;
    }

    setCreatingJobs(true);

    try {
      let createdCount = 0;
      
      // Address Specific - create job with specific addresses
      if (newJob.delivery_type === 'address_specific') {
        const coordAddresses = parsedAddresses.filter(a => a.lat && a.lng);
        let boundary = [];
        let center = [40.7128, -74.006];
        
        if (coordAddresses.length > 0) {
          // Create a bounding box from the addresses
          const lats = coordAddresses.map(a => a.lat);
          const lngs = coordAddresses.map(a => a.lng);
          const minLat = Math.min(...lats);
          const maxLat = Math.max(...lats);
          const minLng = Math.min(...lngs);
          const maxLng = Math.max(...lngs);
          
          // Add padding
          const latPad = (maxLat - minLat) * 0.1 || 0.01;
          const lngPad = (maxLng - minLng) * 0.1 || 0.01;
          
          boundary = [
            [minLat - latPad, minLng - lngPad],
            [minLat - latPad, maxLng + lngPad],
            [maxLat + latPad, maxLng + lngPad],
            [maxLat + latPad, minLng - lngPad]
          ];
          
          center = [(minLat + maxLat) / 2, (minLng + maxLng) / 2];
        }
        
        const zoneResponse = await axios.post(`${API}/admin/zones`, {
          name: `${newJob.title} Zone`,
          description: `Address-specific delivery for ${newJob.client} (${parsedAddresses.length} locations)`,
          boundary: boundary,
          center: center
        }, getAuthHeader());

        const zoneId = zoneResponse.data.id;

        await axios.post(`${API}/admin/jobs`, {
          ...newJob,
          delivery_zone_id: zoneId,
          specific_addresses: parsedAddresses,
          deliveries: parsedAddresses.length
        }, getAuthHeader());
        
        createdCount = 1;
        
      } else if (usesCircle) {
        // Cars/Hands - create single job with circle zone
        const radiusMeters = circleRadius * 1609.34;
        // Generate circle boundary as polygon points for storage
        const circleBoundary = [];
        const numPoints = 32;
        for (let i = 0; i < numPoints; i++) {
          const angle = (i / numPoints) * 2 * Math.PI;
          const lat = circleCenter[0] + (radiusMeters / 111320) * Math.cos(angle);
          const lng = circleCenter[1] + (radiusMeters / (111320 * Math.cos(circleCenter[0] * Math.PI / 180))) * Math.sin(angle);
          circleBoundary.push([lat, lng]);
        }
        
        const zoneResponse = await axios.post(`${API}/admin/zones`, {
          name: `${newJob.title} Zone`,
          description: `${currentDeliveryType.label} delivery for ${newJob.client} (${circleRadius} mile radius)`,
          boundary: circleBoundary,
          center: circleCenter
        }, getAuthHeader());

        const zoneId = zoneResponse.data.id;

        await axios.post(`${API}/admin/jobs`, {
          ...newJob,
          delivery_zone_id: zoneId
        }, getAuthHeader());
        
        createdCount = 1;
        
      } else {
        // Houses and Apartments - use grid sections for multiple sub-jobs
        const sectionsToCreate = gridSections.length > 0 
          ? gridSections 
          : [{
              boundary: boundaryPoints,
              houseCount: houseCount || newJob.deliveries,
              index: 1,
              label: 'A1'
            }];

        const deliveryUnit = newJob.delivery_type === 'apartments' ? 'doors' : 'houses';

        for (const section of sectionsToCreate) {
          const center = calculateCenter(section.boundary);
          const sectionTitle = sectionsToCreate.length > 1 
            ? `${newJob.title} - Zone ${section.label}`
            : newJob.title;
          
          const zoneResponse = await axios.post(`${API}/admin/zones`, {
            name: `${sectionTitle} Zone`,
            description: `${newJob.delivery_type === 'apartments' ? 'Apartment' : 'House'} delivery for ${newJob.client} (~${section.houseCount} ${deliveryUnit})`,
            boundary: section.boundary,
            center: center
          }, getAuthHeader());

          const zoneId = zoneResponse.data.id;

          await axios.post(`${API}/admin/jobs`, {
            ...newJob,
            title: sectionTitle,
            deliveries: section.houseCount,
            delivery_zone_id: zoneId
          }, getAuthHeader());
          
          createdCount++;
        }
      }
      
      toast.success(`Created ${createdCount} job${createdCount > 1 ? 's' : ''} successfully`);
      setShowCreateModal(false);
      resetForm();
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create job(s)');
    } finally {
      setCreatingJobs(false);
    }
  };

  // Generate PDF of delivery zone for customer approval
  const handleDownloadZonePDF = async () => {
    if (!mapContainerRef.current) {
      toast.error('Map not ready for PDF generation');
      return;
    }
    
    setGeneratingPDF(true);
    setHideGridsForPDF(true); // Hide grids for clean PDF
    toast.info('Generating PDF...');
    
    try {
      // Wait for grids to hide and map tiles to load
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const canvas = await html2canvas(mapContainerRef.current, {
        useCORS: true,
        allowTaint: true,
        scale: 2,
        logging: false,
        ignoreElements: (element) => {
          // Ignore all markers
          return element.classList?.contains('grid-label-marker') || 
                 element.classList?.contains('leaflet-marker-icon') ||
                 element.classList?.contains('leaflet-marker-pane');
        }
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('landscape', 'mm', 'a4');
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      // Title
      pdf.setFontSize(22);
      pdf.setTextColor(88, 80, 236); // Purple
      pdf.text('Delivery Zone Proposal', 14, 18);
      
      // Job Info
      pdf.setFontSize(12);
      pdf.setTextColor(60, 60, 60);
      pdf.text(`Client: ${newJob.client || 'TBD'}`, 14, 30);
      pdf.text(`Location: ${newJob.location || 'TBD'}`, 14, 38);
      
      // Delivery stats on the right
      pdf.setFontSize(12);
      pdf.setTextColor(0, 0, 0);
      const deliveryType = newJob.delivery_type?.charAt(0).toUpperCase() + newJob.delivery_type?.slice(1) || 'Houses';
      pdf.text(`Delivery Type: ${deliveryType}`, pageWidth - 90, 30);
      
      const totalCount = houseCount || newJob.deliveries || 0;
      const countLabel = newJob.delivery_type === 'apartments' ? 'Total Doors' : 'Total Houses';
      pdf.setFontSize(14);
      pdf.setTextColor(88, 80, 236);
      pdf.text(`${countLabel}: ${totalCount.toLocaleString()}`, pageWidth - 90, 38);
      
      // Map image - larger since no grid breakdown needed
      const mapX = 14;
      const mapY = 48;
      const imgWidth = pageWidth - 28;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const finalImgHeight = Math.min(imgHeight, pageHeight - mapY - 18);
      
      pdf.addImage(imgData, 'PNG', mapX, mapY, imgWidth, finalImgHeight);
      
      // Footer
      pdf.setFontSize(8);
      pdf.setTextColor(128, 128, 128);
      pdf.text(`Generated: ${new Date().toLocaleString()}`, 14, pageHeight - 8);
      pdf.text('Local Flyer Delivery - Delivery Zone Proposal', pageWidth - 70, pageHeight - 8);
      
      // Download
      const filename = `delivery-zone-${newJob.client || 'proposal'}-${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(filename);
      
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setGeneratingPDF(false);
      setHideGridsForPDF(false); // Show grids again
    }
  };

  // Open the customer tracking page in a new browser tab. Previously copied
  // the URL to clipboard — admins complained that meant an extra paste step.
  const copyShareableLink = (jobId) => {
    const link = `${window.location.origin}/track/${jobId}`;
    const win = window.open(link, '_blank', 'noopener,noreferrer');
    if (!win) {
      // Pop-up blocker fired — fall back to clipboard so the link isn't lost.
      navigator.clipboard.writeText(link);
      toast.error('Pop-up blocked — tracking link copied to clipboard instead. Allow pop-ups for this site to auto-open.');
      return;
    }
    toast.success('Customer tracking page opened in a new tab.');
  };

  // Copy zone preview link to clipboard for existing job
  const copyJobZonePreviewLink = async (job) => {
    if (!job.delivery_zone_id) {
      toast.error('No zone preview available for this job');
      return;
    }
    const link = `${window.location.origin}/zone-preview/${job.delivery_zone_id}`;
    navigator.clipboard.writeText(link);
    toast.success('Zone preview link copied!');
  };

  // Approve a pending_approval job (make it live)
  const handleApprovePendingJob = async (jobId, jobTitle) => {
    try {
      await axios.put(`${API}/admin/jobs/${jobId}/approve`, {}, getAuthHeader());
      toast.success(`Job "${jobTitle}" approved and now live!`);
      fetchJobs();
    } catch (error) {
      console.error('Error approving job:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve job');
    }
  };

  // Reject a pending_approval job
  const handleRejectPendingJob = async (jobId, jobTitle) => {
    if (!window.confirm(`Are you sure you want to reject "${jobTitle}"?`)) return;
    try {
      await axios.put(`${API}/admin/jobs/${jobId}/reject`, {}, getAuthHeader());
      toast.success(`Job "${jobTitle}" rejected`);
      fetchJobs();
    } catch (error) {
      console.error('Error rejecting job:', error);
      toast.error(error.response?.data?.detail || 'Failed to reject job');
    }
  };

  // Un-reject (re-approve) a previously rejected job. Hits the same
  // `/approve` endpoint — the backend detects the `rejected` previous
  // status and (a) clears rejection metadata, (b) restores admin_approved,
  // (c) sets status to `open` if the client had already approved or
  // `pending_approval` otherwise, and (d) fans out an in-app bell to every
  // admin + manager and a branded email to the client.
  const handleUnrejectJob = async (jobId, jobTitle) => {
    if (!window.confirm(
      `Re-approve "${jobTitle}"?\n\n` +
      `This will lift the rejection, notify the client by email, and ping every admin + manager in the bell tray.`
    )) return;
    try {
      const { data } = await axios.put(
        `${API}/admin/jobs/${jobId}/approve`,
        {},
        getAuthHeader(),
      );
      const live = data?.status === 'open';
      toast.success(
        live
          ? `"${jobTitle}" re-approved — now live for workers. Client notified.`
          : `"${jobTitle}" re-approved — pending client approval. Client notified.`
      );
      fetchJobs();
    } catch (error) {
      console.error('Error re-approving job:', error);
      toast.error(error.response?.data?.detail || 'Failed to re-approve job');
    }
  };

  // Reactivate (un-expire) a job. The backend endpoint preserves whichever
  // side (client / admin) already approved before expiration and issues a
  // new 7-day window. If the client never approved, a fresh client token
  // is generated and the email regenerated; if only the admin side was
  // missing, this just flips the job back to pending_approval so the
  // admin Approve button reappears in the row.
  const handleReactivateJob = async (job) => {
    const clientOk = !!job.client_approved;
    const adminOk = !!job.admin_approved;
    const lines = [
      `Reactivate "${job.title}"?`,
      '',
      `• Client approved already: ${clientOk ? 'yes (preserved)' : 'no (a fresh approval link will be sent)'}`,
      `• Admin approved already: ${adminOk ? 'yes (preserved)' : 'no (you can approve from the row after reactivation)'}`,
      '',
      'A new 7-day approval window will start.',
    ].join('\n');
    if (!window.confirm(lines)) return;
    try {
      const res = await axios.post(
        `${API}/admin/opportunities/${job.id}/reactivate`,
        {},
        getAuthHeader(),
      );
      const needsAdmin = !!res.data?.needs_admin_approval;
      const needsClient = !!res.data?.needs_client_approval;
      let msg = `Job "${job.title}" reactivated.`;
      if (!needsAdmin && !needsClient) msg += ' Already approved by both sides — flip to open via Approve.';
      else if (needsAdmin && !needsClient) msg += ' Click "Approve" on the row to open it for workers.';
      else if (!needsAdmin && needsClient) msg += ' Client still needs to approve via the email link.';
      else msg += ' Both admin and client need to re-approve.';
      toast.success(msg);
      fetchJobs();
    } catch (error) {
      console.error('Error reactivating job:', error);
      toast.error(error.response?.data?.detail || 'Failed to reactivate job');
    }
  };

  // Approve a user's job request
  const handleApproveUserRequest = async (jobId, jobTitle) => {
    try {
      const response = await axios.put(`${API}/admin/jobs/${jobId}/approve-request`, {}, getAuthHeader());
      toast.success(response.data.message || `Request approved for "${jobTitle}"`);
      fetchJobs();
    } catch (error) {
      console.error('Error approving user request:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    }
  };

  // Deny a user's job request
  const handleDenyUserRequest = async (jobId, jobTitle) => {
    const reason = window.prompt(`Optionally provide a reason for denying this request for "${jobTitle}":`);
    if (reason === null) return; // User cancelled
    
    try {
      await axios.put(`${API}/admin/jobs/${jobId}/deny-request`, { reason: reason || null }, getAuthHeader());
      toast.success(`Request denied for "${jobTitle}"`);
      fetchJobs();
    } catch (error) {
      console.error('Error denying user request:', error);
      toast.error(error.response?.data?.detail || 'Failed to deny request');
    }
  };

  // Open edit progress modal
  const handleOpenEditProgress = async (job) => {
    setEditProgressData({
      deliveries_count: 0,
      distance: 0,
      notes: ''
    });
    setProgressDetails(null);
    setShowEditProgressModal(true);
    
    // Fetch current progress details
    try {
      const response = await axios.get(`${API}/admin/jobs/${job.id}/progress-details`, getAuthHeader());
      setProgressDetails(response.data);
      // Pre-fill with current progress
      setEditProgressData({
        deliveries_count: response.data.tracking_progress?.deliveries_count || 0,
        distance: response.data.tracking_progress?.distance || 0,
        notes: response.data.tracking_progress?.admin_notes || ''
      });
    } catch (error) {
      console.error('Error fetching progress details:', error);
    }
  };

  // Save edited progress
  const handleSaveProgress = async () => {
    if (!selectedJobDetail) return;
    
    setSavingProgress(true);
    try {
      const response = await axios.put(
        `${API}/admin/jobs/${selectedJobDetail.id}/update-progress`,
        {
          deliveries_count: parseInt(editProgressData.deliveries_count) || 0,
          distance: parseFloat(editProgressData.distance) || 0,
          notes: editProgressData.notes || null
        },
        getAuthHeader()
      );
      toast.success(response.data.message || 'Progress updated successfully');
      setShowEditProgressModal(false);
      
      // Refresh job details
      if (selectedJobDetail) {
        handleViewJobDetail(selectedJobDetail);
      }
    } catch (error) {
      console.error('Error updating progress:', error);
      toast.error(error.response?.data?.detail || 'Failed to update progress');
    } finally {
      setSavingProgress(false);
    }
  };

  // Generate and copy preview link for the entire delivery zone (before job creation)
  const copyZonePreviewLink = async () => {
    if (boundaryPoints.length < 3 && !circleCenter) {
      toast.error('Draw a delivery zone first');
      return;
    }
    
    try {
      // Store the zone preview data temporarily in the database
      const previewData = {
        client: newJob.client || 'Preview',
        location: newJob.location || '',
        delivery_type: newJob.delivery_type,
        boundary: boundaryPoints.length >= 3 ? boundaryPoints : null,
        circle_center: circleCenter,
        circle_radius: circleRadius,
        total_deliveries: houseCount || newJob.deliveries,
        grid_sections: gridSections.map(g => ({ label: g.label, count: g.houseCount, center: g.center })),
        created_at: new Date().toISOString()
      };
      
      const response = await axios.post(`${API}/admin/zone-preview`, previewData, getAuthHeader());
      const previewId = response.data.id;
      
      const link = `${window.location.origin}/zone-preview/${previewId}`;
      navigator.clipboard.writeText(link);
      toast.success('Full zone preview link copied to clipboard!');
    } catch (error) {
      console.error('Error creating preview:', error);
      toast.error('Failed to create preview link');
    }
  };

  const resetForm = () => {
    setNewJob({
      title: '',
      client: '',
      client_contact_name: '',
      client_phone: '',
      client_email: '',
      location: '',
      delivery_type: 'houses',
      deliveries: 100,
      pay_per_flyer: 0.50,
      client_payment: 0,
      bonus_pay: 0,
      deadline: '',
      flyer_type: '',
      description: '',
      image_url: ''
    });
    setBoundaryPoints([]);
    setCircleCenter(null);
    setCircleRadius(DEFAULT_RADIUS_MILES);
    setDrawingMode(false);
    setHouseCount(null);
    setAddressBreakdown(null);
    setGridSections([]);
    setApartmentComplexes([]);
  };

  const handleEditJob = (job) => {
    setEditingJob({
      ...job,
      client_contact_name: job.client_contact_name || '',
      client_phone: job.client_phone || '',
      client_email: job.client_email || ''
    });
    setShowEditModal(true);
  };

  const handleUpdateJob = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/jobs/${editingJob.id}`, {
        title: editingJob.title,
        client: editingJob.client,
        client_contact_name: editingJob.client_contact_name,
        client_phone: editingJob.client_phone,
        client_email: editingJob.client_email,
        location: editingJob.location,
        deliveries: editingJob.deliveries,
        pay_per_flyer: editingJob.pay_per_flyer,
        deadline: editingJob.deadline,
        flyer_type: editingJob.flyer_type,
        description: editingJob.description,
        status: editingJob.status
      }, getAuthHeader());
      
      toast.success('Job updated successfully');
      setShowEditModal(false);
      setEditingJob(null);
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update job');
    }
  };

  const handleDeleteJob = async (jobId, jobTitle, hasDeliverySessions = false) => {
    // Check if the job has delivery sessions - if so, offer force delete option
    if (hasDeliverySessions) {
      const forceDelete = window.confirm(
        `"${jobTitle}" has active delivery sessions.\n\n` +
        `As an administrator, you can force delete this job and all its delivery data.\n\n` +
        `Click OK to force delete, or Cancel to abort.`
      );
      
      if (!forceDelete) {
        return;
      }
      
      try {
        await axios.delete(`${API}/admin/jobs/${jobId}?force=true`, getAuthHeader());
        toast.success('Job and delivery sessions deleted successfully');
        fetchJobs();
        setSelectedJobDetail(null);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to delete job');
      }
      return;
    }
    
    // Normal delete for jobs without sessions
    if (!window.confirm(`Are you sure you want to delete "${jobTitle}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/jobs/${jobId}`, getAuthHeader());
      toast.success('Job deleted successfully');
      fetchJobs();
    } catch (error) {
      // Check if error is about delivery sessions - offer force delete
      const errorMsg = error.response?.data?.detail || '';
      if (errorMsg.includes('delivery sessions') && errorMsg.includes('Administrators can use force delete')) {
        const forceDelete = window.confirm(
          `${errorMsg}\n\nAs an administrator, would you like to force delete this job and all its delivery data?`
        );
        
        if (forceDelete) {
          try {
            await axios.delete(`${API}/admin/jobs/${jobId}?force=true`, getAuthHeader());
            toast.success('Job and delivery sessions force deleted successfully');
            fetchJobs();
            setSelectedJobDetail(null);
          } catch (forceError) {
            toast.error(forceError.response?.data?.detail || 'Failed to force delete job');
          }
        }
      } else {
        toast.error(errorMsg || 'Failed to delete job');
      }
    }
  };

  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (selectedJobs.size === 0) return;
    
    const confirmed = window.confirm(
      `Are you sure you want to delete ${selectedJobs.size} selected job(s)?\n\nJobs with delivery sessions will be skipped unless you choose to force delete.\n\nThis action cannot be undone.`
    );
    if (!confirmed) return;
    
    setBulkDeleting(true);
    try {
      const response = await axios.post(`${API}/admin/jobs/bulk-delete`, {
        job_ids: Array.from(selectedJobs),
        force: false
      }, getAuthHeader());
      
      const { deleted, skipped } = response.data;
      
      if (skipped.length > 0) {
        const forceDelete = window.confirm(
          `Deleted ${deleted} jobs.\n\n${skipped.length} job(s) were skipped because they have delivery sessions:\n${skipped.map(s => `• ${s.title || s.id}`).join('\n')}\n\nForce delete these too?`
        );
        
        if (forceDelete) {
          const skippedIds = skipped.map(s => s.id);
          const forceResponse = await axios.post(`${API}/admin/jobs/bulk-delete`, {
            job_ids: skippedIds,
            force: true
          }, getAuthHeader());
          toast.success(`Deleted ${deleted + forceResponse.data.deleted} jobs total`);
        } else {
          toast.success(`Deleted ${deleted} jobs (${skipped.length} skipped)`);
        }
      } else {
        toast.success(`Deleted ${deleted} jobs successfully`);
      }
      
      setSelectedJobs(new Set());
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete jobs');
    } finally {
      setBulkDeleting(false);
    }
  };

  // Bulk mark complete handler
  const handleBulkMarkComplete = async () => {
    if (selectedJobs.size === 0) return;
    
    const confirmed = window.confirm(
      `Mark ${selectedJobs.size} selected job(s) as completed?\n\nJobs already completed or archived will be skipped.`
    );
    if (!confirmed) return;
    
    setBulkCompleting(true);
    try {
      const response = await axios.post(`${API}/admin/jobs/bulk-mark-complete`, {
        job_ids: Array.from(selectedJobs)
      }, getAuthHeader());
      
      const { completed, skipped } = response.data;
      if (skipped.length > 0) {
        toast.success(`Marked ${completed} jobs complete (${skipped.length} skipped)`);
      } else {
        toast.success(`Marked ${completed} jobs as complete`);
      }
      
      setSelectedJobs(new Set());
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark jobs complete');
    } finally {
      setBulkCompleting(false);
    }
  };

  // Toggle job selection
  const toggleJobSelection = (jobId) => {
    setSelectedJobs(prev => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  };

  // Toggle select all (for current PAGE of jobs only)
  const toggleSelectAll = () => {
    const currentPageIds = paginatedJobs.map(j => j.id);
    const allSelected = currentPageIds.every(id => selectedJobs.has(id));
    if (allSelected) {
      // Deselect only current page jobs
      setSelectedJobs(prev => {
        const next = new Set(prev);
        currentPageIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      // Select current page jobs (add to existing selection)
      setSelectedJobs(prev => {
        const next = new Set(prev);
        currentPageIds.forEach(id => next.add(id));
        return next;
      });
    }
  };

  // Clone job - navigate to create job page with pre-filled data
  const handleCloneJob = (job) => {
    // Store job data in sessionStorage for the create page to pick up
    const cloneData = {
      id: job.id, // Include original job ID to load house locations
      title: `${job.title} (Copy)`,
      job_title: `${job.title} (Copy)`,
      client: job.client || '',
      client_contact_name: job.client_contact_name || '',
      client_phone: job.client_phone || '',
      client_email: job.client_email || '',
      delivery_type: job.delivery_type || 'houses',
      pay_per_flyer: job.pay_per_flyer || 0.25,
      bonus_pay: job.bonus_pay || 0,
      deadline: '', // Don't copy deadline
      flyer_type: job.flyer_type || 'Standard',
      description: job.description || '',
      deliveries: job.deliveries || 0,
      client_payment: job.client_payment || 0,
      client_cost_per_delivery: job.client_cost_per_delivery || 0,
      pickup_location: job.pickup_location || '',
      pickup_date: '', // Don't copy pickup date
      pickup_time: job.pickup_time || '',
      // Zone data - support both single boundary and multi-polygon
      delivery_boundary: job.delivery_boundary || [],
      delivery_polygons: job.delivery_polygons || [],
      // Circle-based zones (for Cars, Hands delivery types)
      circle_center: job.circle_center,
      circle_radius_miles: job.circle_radius_miles,
      // Map center for positioning
      map_center: job.map_center,
      // Other zone data
      specific_addresses: job.specific_addresses || [],
      grid_sections: job.grid_sections || []
    };
    
    sessionStorage.setItem('cloneJobData', JSON.stringify(cloneData));
    toast.success('Job data copied! Redirecting to Create Job page...');
    
    // Navigate to create job page using correct route
    navigate('/admin/create-job?clone=true');
  };

  // Sutherland-Hodgman polygon clipping: clip `subject` (parent polygon, irregular)
  // against `clip` (convex rectangle). Polygons are arrays of [lat, lng] points.
  // Returns the intersection polygon (may be empty if no overlap).
  const clipPolygonToRect = (subject, rectMinLat, rectMaxLat, rectMinLng, rectMaxLng) => {
    if (!subject || subject.length < 3) return [];
    // Clip edges: left (lng >= minLng), right (lng <= maxLng),
    // bottom (lat >= minLat), top (lat <= maxLat)
    const edges = [
      { type: 'left',   inside: (p) => p[1] >= rectMinLng, intersect: (a, b) => { const t = (rectMinLng - a[1]) / (b[1] - a[1]); return [a[0] + t * (b[0] - a[0]), rectMinLng]; } },
      { type: 'right',  inside: (p) => p[1] <= rectMaxLng, intersect: (a, b) => { const t = (rectMaxLng - a[1]) / (b[1] - a[1]); return [a[0] + t * (b[0] - a[0]), rectMaxLng]; } },
      { type: 'bottom', inside: (p) => p[0] >= rectMinLat, intersect: (a, b) => { const t = (rectMinLat - a[0]) / (b[0] - a[0]); return [rectMinLat, a[1] + t * (b[1] - a[1])]; } },
      { type: 'top',    inside: (p) => p[0] <= rectMaxLat, intersect: (a, b) => { const t = (rectMaxLat - a[0]) / (b[0] - a[0]); return [rectMaxLat, a[1] + t * (b[1] - a[1])]; } },
    ];
    let output = subject.slice();
    for (const edge of edges) {
      if (output.length === 0) break;
      const input = output;
      output = [];
      for (let i = 0; i < input.length; i++) {
        const curr = input[i];
        const prev = input[(i - 1 + input.length) % input.length];
        const currIn = edge.inside(curr);
        const prevIn = edge.inside(prev);
        if (currIn) {
          if (!prevIn) output.push(edge.intersect(prev, curr));
          output.push(curr);
        } else if (prevIn) {
          output.push(edge.intersect(prev, curr));
        }
      }
    }
    return output;
  };

  // Shoelace area for a polygon of [lat, lng] points (degrees^2; relative measure only)
  const polygonArea = (poly) => {
    if (!poly || poly.length < 3) return 0;
    let a = 0;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      a += (poly[j][1] + poly[i][1]) * (poly[j][0] - poly[i][0]);
    }
    return Math.abs(a / 2);
  };

  // Split approved job into multiple grid-based jobs
  const handleSplitIntoGridJobs = async (job, customSections = null) => {
    const isAddressType = job.delivery_type === 'address_specific' || job.delivery_type === 'canvassing';
    const geocodedAddresses = isAddressType 
      ? (job.specific_addresses || []).filter(a => a.lat && a.lng)
      : [];
    
    // Validation
    if (!isAddressType && (!job.delivery_boundary || job.delivery_boundary.length < 3)) {
      toast.error('This job does not have a valid delivery boundary to split');
      return;
    }
    if (isAddressType && geocodedAddresses.length < 2) {
      toast.error('Need at least 2 geocoded addresses to split');
      return;
    }

    const totalCount = isAddressType ? geocodedAddresses.length : (job.deliveries || 100);
    const payPerFlyer = job.pay_per_flyer || 0.25;
    const targetPerGrid = 450;
    // When customSections is provided (from the modal), honor it. Otherwise
    // auto-calculate from the targetPerGrid heuristic.
    const numSections = customSections != null
      ? Math.max(2, Math.min(64, parseInt(customSections, 10) || 2))
      : Math.ceil(totalCount / targetPerGrid);
    
    if (numSections <= 1) {
      toast.info('This job is already within the target range. No splitting needed.');
      return;
    }

    // Calculate bounding box
    let minLat, maxLat, minLng, maxLng;
    if (isAddressType) {
      const lats = geocodedAddresses.map(a => a.lat);
      const lngs = geocodedAddresses.map(a => a.lng);
      minLat = Math.min(...lats); maxLat = Math.max(...lats);
      minLng = Math.min(...lngs); maxLng = Math.max(...lngs);
      // Add small padding
      const padLat = (maxLat - minLat) * 0.02 || 0.001;
      const padLng = (maxLng - minLng) * 0.02 || 0.001;
      minLat -= padLat; maxLat += padLat;
      minLng -= padLng; maxLng += padLng;
    } else {
      const boundary = job.delivery_boundary;
      const lats = boundary.map(p => p[0]);
      const lngs = boundary.map(p => p[1]);
      minLat = Math.min(...lats); maxLat = Math.max(...lats);
      minLng = Math.min(...lngs); maxLng = Math.max(...lngs);
    }

    // Find best rows × cols combination
    const avgLat = (minLat + maxLat) / 2;
    const latCorrectionFactor = Math.cos(avgLat * Math.PI / 180);
    const physicalWidth = (maxLng - minLng) * latCorrectionFactor;
    const physicalHeight = maxLat - minLat;
    
    let bestRows = 1, bestCols = numSections, bestDiff = Infinity;
    for (let r = 1; r <= numSections; r++) {
      const c = Math.ceil(numSections / r);
      const total = r * c;
      const cellWidth = physicalWidth / c;
      const cellHeight = physicalHeight / r;
      const cellRatio = cellWidth / cellHeight;
      const squareness = Math.abs(Math.log(cellRatio));
      const diff = (total - numSections) * 3 + squareness * 5;
      if (diff < bestDiff) {
        bestDiff = diff;
        bestRows = r;
        bestCols = c;
      }
    }
    const rows = bestRows;
    const cols = bestCols;
    const latStep = (maxLat - minLat) / rows;
    const lngStep = (maxLng - minLng) / cols;
    
    const grids = [];
    
    // Point-in-polygon check (only used for polygon-based jobs)
    const pointInPoly = (point, poly) => {
      const [lat, lng] = point;
      let inside = false;
      for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const xi = poly[i][0], yi = poly[i][1];
        const xj = poly[j][0], yj = poly[j][1];
        if ((yi > lng) !== (yj > lng) && lat < (xj - xi) * (lng - yi) / (yj - yi) + xi) {
          inside = !inside;
        }
      }
      return inside;
    };
    
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const gridMinLat = minLat + row * latStep;
        const gridMaxLat = minLat + (row + 1) * latStep;
        const gridMinLng = minLng + col * lngStep;
        const gridMaxLng = minLng + (col + 1) * lngStep;
        
        // For polygon jobs: clip the rectangle against the parent polygon so the
        // child boundary follows the parent's actual shape (no more squares
        // bleeding outside the zone). For address jobs: use the rectangle as-is.
        let gridBoundary;
        if (!isAddressType && job.delivery_boundary && job.delivery_boundary.length >= 3) {
          const clipped = clipPolygonToRect(
            job.delivery_boundary,
            gridMinLat, gridMaxLat, gridMinLng, gridMaxLng
          );
          // Skip cells with no meaningful intersection
          if (clipped.length < 3 || polygonArea(clipped) <= 0) continue;
          gridBoundary = clipped;
        } else {
          gridBoundary = [
            [gridMinLat, gridMinLng],
            [gridMinLat, gridMaxLng],
            [gridMaxLat, gridMaxLng],
            [gridMaxLat, gridMinLng],
          ];
        }
        
        // Center: use centroid of the (possibly clipped) child polygon
        const cLats = gridBoundary.map(p => p[0]);
        const cLngs = gridBoundary.map(p => p[1]);
        const center = [
          cLats.reduce((s, v) => s + v, 0) / cLats.length,
          cLngs.reduce((s, v) => s + v, 0) / cLngs.length,
        ];
        
        const label = `${String.fromCharCode(65 + row)}${col + 1}`;
        
        // For address jobs, find which addresses fall into this cell
        let cellAddresses = [];
        if (isAddressType) {
          cellAddresses = geocodedAddresses.filter(a => 
            a.lat >= gridMinLat && a.lat <= gridMaxLat && a.lng >= gridMinLng && a.lng <= gridMaxLng
          );
          if (cellAddresses.length === 0) continue; // Skip empty cells
        }
        
        grids.push({
          boundary: gridBoundary,
          // Store the rectangle bounds too — used later to filter cached house
          // locations against both the rectangle AND the parent polygon.
          rectBounds: { minLat: gridMinLat, maxLat: gridMaxLat, minLng: gridMinLng, maxLng: gridMaxLng },
          houseCount: isAddressType ? cellAddresses.length : Math.round(totalCount / numSections),
          label,
          center,
          cellAddresses: isAddressType ? cellAddresses : [] // Store addresses for this cell
        });
      }
    }
    
    if (grids.length === 0) {
      toast.error('No valid grid sections could be created');
      setSplittingIntoGrids(false);
      return;
    }

    // For non-address jobs: redistribute and fetch house locations
    if (!isAddressType) {
      // Redistribute house counts proportionally
      const perGrid = Math.round(totalCount / grids.length);
      grids.forEach(g => g.houseCount = perGrid);
      const assignedTotal = grids.reduce((sum, g) => sum + g.houseCount, 0);
      if (assignedTotal !== totalCount) {
        grids[grids.length - 1].houseCount += (totalCount - assignedTotal);
      }
    }

    // Show initial confirmation
    const avgPerGrid = Math.round(totalCount / grids.length);
    const initialConfirm = window.confirm(
      `This will split "${job.title}" into ${grids.length} grid sections (~${avgPerGrid} ${isAddressType ? 'addresses' : 'houses'} each).\n\n` +
      (isAddressType 
        ? `Each child job will contain the actual addresses that fall within its grid cell.\n\n`
        : `The system will now calculate actual house counts for each grid from cached locations.\n\n`) +
      `Proceed?`
    );

    if (!initialConfirm) return;

    setSplittingIntoGrids(true);

    // For non-address jobs, try to refine house counts from cached locations
    if (!isAddressType) {
      toast.info('Calculating actual house counts for each grid section...', { duration: 5000 });
    try {
      let parentHouseLocations = [];
      
      // First try to get cached houses from the parent job
      try {
        const cacheResponse = await axios.get(`${API}/admin/jobs/${job.id}/house-locations`, getAuthHeader());
        if (cacheResponse.data && cacheResponse.data.houses) {
          parentHouseLocations = cacheResponse.data.houses;
          console.log(`Got ${parentHouseLocations.length} cached houses from parent job`);
        }
      } catch (err) {
        console.log('Could not fetch parent job house cache:', err.message);
      }
      
      if (parentHouseLocations.length > 0) {
        // First, filter cached locations to ONLY those inside the parent polygon.
        // This prevents counting houses that fall inside a grid rectangle but
        // outside the actual parent zone (which used to inflate child counts).
        const parentPoly = job.delivery_boundary;
        const housesInParent = parentPoly && parentPoly.length >= 3
          ? parentHouseLocations.filter(h => {
              const lat = h.lat || h[0];
              const lng = h.lon || h.lng || h[1];
              return pointInPoly([lat, lng], parentPoly);
            })
          : parentHouseLocations;

        // Filter house locations into each grid cell (rectangle bounds is enough
        // now since we already restricted to the parent polygon above).
        grids.forEach((grid) => {
          const rb = grid.rectBounds;
          const housesInCell = housesInParent.filter(h => {
            const lat = h.lat || h[0];
            const lng = h.lon || h.lng || h[1];
            return lat >= rb.minLat && lat <= rb.maxLat && lng >= rb.minLng && lng <= rb.maxLng;
          });
          
          // Store the cached dot locations for map display
          grid.houseLocations = housesInCell;
          // Store the raw dot count (for proportion calculation)
          grid.dotCount = housesInCell.length;
          grid.hasActualCount = true;
        });
        
        // Remove grids with 0 dots
        const nonEmptyGrids = grids.filter(g => g.dotCount > 0);
        if (nonEmptyGrids.length < grids.length) {
          const removedCount = grids.length - nonEmptyGrids.length;
          toast.info(`Removed ${removedCount} empty grid sections with no houses`);
          grids.length = 0;
          grids.push(...nonEmptyGrids);
        }
        
        // Second pass: use PROPORTIONAL distribution based on parent job's delivery_count
        // This ensures grid jobs have accurate house counts even when cached dots are sparse
        const totalDots = grids.reduce((sum, g) => sum + g.dotCount, 0);
        const parentDeliveryCount = job.deliveries || totalCount;
        
        let assignedTotal = 0;
        grids.forEach((grid, index) => {
          // Calculate this grid's share based on proportion of cached dots
          const proportion = totalDots > 0 ? grid.dotCount / totalDots : 1 / grids.length;
          const proportionalCount = Math.round(parentDeliveryCount * proportion);
          grid.houseCount = proportionalCount;
          assignedTotal += proportionalCount;
        });
        
        // Adjust last grid to ensure total matches exactly (handle rounding)
        if (grids.length > 0 && assignedTotal !== parentDeliveryCount) {
          grids[grids.length - 1].houseCount += (parentDeliveryCount - assignedTotal);
        }
        
        const finalTotal = grids.reduce((sum, g) => sum + g.houseCount, 0);
        toast.success(`Distributed ${finalTotal} houses across ${grids.length} grid sections (based on ${totalDots} mapped locations)`);
      } else {
        // No cached houses available - fall back to area-proportional distribution
        // using the (already clipped) child polygon area so children get a count
        // proportional to how much of the parent zone they actually cover.
        const parentDeliveryCount = job.deliveries || totalCount;
        const areas = grids.map(g => polygonArea(g.boundary));
        const totalArea = areas.reduce((s, v) => s + v, 0);
        let assignedTotal = 0;
        grids.forEach((grid, idx) => {
          const proportion = totalArea > 0 ? areas[idx] / totalArea : 1 / grids.length;
          grid.houseCount = Math.round(parentDeliveryCount * proportion);
          assignedTotal += grid.houseCount;
        });
        if (grids.length > 0 && assignedTotal !== parentDeliveryCount) {
          grids[grids.length - 1].houseCount += (parentDeliveryCount - assignedTotal);
        }
        toast.warning('No cached house locations found. Using area-proportional estimates.');
      }
      
    } catch (err) {
      console.error('Error fetching house counts:', err);
      toast.warning('Using estimated house counts based on area distribution');
    }
    } // end if (!isAddressType) block for house location cache
    
    // Show final confirmation with actual counts
    const finalConfirm = window.confirm(
      `Ready to create ${grids.length} grid jobs with the following ${isAddressType ? 'address' : 'house'} counts:\n\n` +
      grids.map(g => `• ${job.title} - ${g.label}: ${g.houseCount} ${isAddressType ? 'addresses' : 'houses'} ($${(g.houseCount * payPerFlyer).toFixed(2)} pay)`).join('\n') +
      `\n\nTotal: ${grids.reduce((sum, g) => sum + g.houseCount, 0)} ${isAddressType ? 'addresses' : 'houses'}\n\nProceed with creating these jobs?`
    );
    
    if (!finalConfirm) {
      setSplittingIntoGrids(false);
      return;
    }
    
    // === Progress transfer (for in-progress parent jobs) ===
    // If the parent already has deliveries completed, fetch them and bucket
    // each one into the matching child grid so the new jobs surface their
    // pre-existing progress. Safe to run even when the parent has no progress
    // — the endpoint just returns empty arrays.
    grids.forEach(g => { g.transferredDelivered = []; g.transferredManual = []; });
    let totalTransferred = 0;
    if (!isAddressType) {
      try {
        const progRes = await axios.get(
          `${API}/admin/jobs/${job.id}/aggregated-progress`,
          getAuthHeader()
        );
        const deliveredWithCoords = progRes.data?.delivered_with_coords || [];
        const manualLocations = progRes.data?.manual_locations || [];

        const assignToGrid = (lat, lng) => {
          for (const g of grids) {
            const rb = g.rectBounds;
            if (!rb) continue;
            if (lat >= rb.minLat && lat <= rb.maxLat && lng >= rb.minLng && lng <= rb.maxLng) {
              return g;
            }
          }
          return null;
        };

        for (const d of deliveredWithCoords) {
          const g = assignToGrid(d.lat, d.lng);
          if (g) { g.transferredDelivered.push(d.id); totalTransferred++; }
        }
        for (const m of manualLocations) {
          const g = assignToGrid(m.lat, m.lng);
          if (g) { g.transferredManual.push(m); totalTransferred++; }
        }

        if (totalTransferred > 0) {
          toast.info(`Transferring ${totalTransferred} already-completed deliveries to the new child jobs...`, { duration: 4000 });
        }
      } catch (err) {
        console.warn('Could not fetch parent progress for transfer:', err.message);
        // Non-fatal — split continues without progress transfer.
      }
    }

    let successCount = 0;
    let failCount = 0;
    let refreshTriggeredCount = 0;

    try {
      for (const grid of grids) {
        try {
          const gridJobData = {
            title: `${job.title} - ${grid.label}`,
            client: job.client,
            client_contact_name: job.client_contact_name,
            client_phone: job.client_phone,
            client_email: job.client_email,
            location: job.location,
            delivery_type: job.delivery_type,
            deliveries: grid.houseCount,
            pay_per_flyer: job.pay_per_flyer,
            deadline: job.deadline || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            flyer_type: job.flyer_type || 'standard',
            pickup_location: job.pickup_location || job.location || 'TBD',
            pickup_date: job.pickup_date || new Date().toISOString().split('T')[0],
            description: `${job.description || ''}\n[Grid Section: ${grid.label}]`.trim(),
            delivery_type: job.delivery_type || 'houses',
            delivery_boundary: grid.boundary,
            map_center: grid.center,
            grid_sections: [grid],
            client_payment: Math.round((grid.houseCount / totalCount) * (job.client_payment || 0)),
            total_pay: grid.houseCount * payPerFlyer,
            status: job.status === 'pending_approval' ? 'pending_approval' : 'open',
            admin_approved: job.status !== 'pending_approval',
            client_approved: job.status !== 'pending_approval',
            parent_job_id: job.id,
            // Address-specific: include the actual addresses for this grid cell
            specific_addresses: isAddressType ? grid.cellAddresses : [],
            // Convert addresses to house_locations format for map display
            house_locations: isAddressType 
              ? (grid.cellAddresses.length < 500 
                  ? grid.cellAddresses.map((a, idx) => ({ id: `addr_${idx}`, lat: a.lat, lon: a.lng, address: a.address || '', source: 'uploaded' }))
                  : undefined)
              : (grid.houseLocations && grid.houseLocations.length > 0 && grid.houseLocations.length < 500 ? grid.houseLocations : undefined)
          };

          const gridJobResponse = await axios.post(`${API}/admin/jobs`, gridJobData, getAuthHeader());
          const gridJobId = gridJobResponse.data?.id;
          
          // Save large house location sets separately
          const largeHouseSet = isAddressType 
            ? (grid.cellAddresses.length >= 500 
                ? grid.cellAddresses.map((a, idx) => ({ id: `addr_${idx}`, lat: a.lat, lon: a.lng, address: a.address || '', source: 'uploaded' }))
                : null)
            : (grid.houseLocations && grid.houseLocations.length >= 500 ? grid.houseLocations : null);
          
          if (largeHouseSet && gridJobId) {
            try {
              await axios.post(
                `${API}/admin/jobs/${gridJobId}/save-house-locations`,
                { houses: largeHouseSet },
                getAuthHeader()
              );
            } catch (saveErr) {
              console.warn(`Could not save house locations for grid ${grid.label}:`, saveErr.message);
            }
          }

          // Seed transferred progress (delivered houses + manual marks that
          // fell inside this grid cell on the parent). No-op when parent had
          // no progress, but always called so the audit trail records intent.
          const hasTransfer = (grid.transferredDelivered && grid.transferredDelivered.length > 0)
            || (grid.transferredManual && grid.transferredManual.length > 0);
          if (gridJobId && hasTransfer) {
            try {
              await axios.post(
                `${API}/admin/jobs/${gridJobId}/seed-progress-from-split`,
                {
                  source_parent_job_id: job.id,
                  delivered_house_ids: grid.transferredDelivered || [],
                  manual_locations: grid.transferredManual || [],
                },
                getAuthHeader()
              );
            } catch (seedErr) {
              console.warn(`Could not seed progress for grid ${grid.label}:`, seedErr.message);
            }
          }

          // Trigger a fresh house-cache rebuild for the child IF the parent's
          // cache fragment for this cell was sparse (fewer dots than this
          // grid's target deliveries). The backend re-queries parcel DB + OSM
          // using the child's clipped polygon, so dots accurately reflect the
          // actual houses inside the child zone. Fire-and-forget — admins
          // who reopen the child later will see a properly populated map.
          if (!isAddressType && gridJobId) {
            const haveDots = (grid.houseLocations || []).length;
            const targetDots = grid.houseCount || 0;
            // Only refresh when noticeably sparse (≤ 50% coverage). When the
            // parent cache was already dense, the inherited dots are fine.
            if (haveDots < Math.max(10, Math.floor(targetDots * 0.5))) {
              refreshTriggeredCount++;
              axios.post(
                `${API}/admin/jobs/${gridJobId}/refresh-house-cache`,
                { force: true },
                getAuthHeader()
              ).catch(refreshErr => {
                console.warn(`Background house-cache refresh for ${grid.label} failed:`, refreshErr?.message);
              });
            }
          }
          successCount++;
        } catch (error) {
          console.error(`Error creating job for grid ${grid.label}:`, error);
          failCount++;
        }
      }

      if (successCount > 0) {
        // Mark the original job as split so users can't select it
        try {
          await axios.post(`${API}/admin/jobs/${job.id}/mark-as-split`, {}, getAuthHeader());
        } catch (error) {
          console.error('Error marking job as split:', error);
          // Don't fail the whole operation, just log the error
        }
        
        const transferNote = totalTransferred > 0
          ? ` (Transferred ${totalTransferred} already-completed deliveries to children.)`
          : '';
        toast.success(`Created ${successCount} grid jobs successfully!${failCount > 0 ? ` (${failCount} failed)` : ''}${transferNote}`);
        // Grid children are auto-hidden from workers so admins can review
        // each cell first — see the "Hidden" red badge + Unhide button on
        // each new row. Make this visible explicitly so admins don't think
        // their jobs vanished.
        toast.info(
          `New grid jobs are hidden from workers until you unhide them. Click the red "Unhide" button on each grid cell you want to publish.`,
          { duration: 9000 }
        );
        if (refreshTriggeredCount > 0) {
          toast.info(
            `Recalculating house locations in the background for ${refreshTriggeredCount} child ${refreshTriggeredCount === 1 ? 'job' : 'jobs'}. ` +
            `Green dots may take ~10-30 seconds to populate — reopen the child job to see them.`,
            { duration: 8000 }
          );
        }
        setShowJobDetail(false);
        fetchJobs();
      } else {
        toast.error('Failed to create any grid jobs');
      }
    } catch (error) {
      console.error('Error splitting job into grids:', error);
      toast.error('Failed to split job into grids');
    } finally {
      setSplittingIntoGrids(false);
    }
  };

  // Send client approval email
  const handleSendApprovalEmail = async (job) => {
    if (!job.client_email) {
      toast.error('No client email address on this job. Please add the client email first.');
      return;
    }

    if (job.client_approved) {
      toast.info('This job has already been approved by the client.');
      return;
    }

    const confirmSend = window.confirm(
      `Send approval link to ${job.client_email}?\n\nJob: ${job.title}\nClient: ${job.client}`
    );

    if (!confirmSend) return;

    setSendingApprovalEmail(prev => ({ ...prev, [job.id]: true }));

    try {
      const response = await axios.post(
        `${API}/admin/jobs/${job.id}/send-client-approval-email`,
        {},
        getAuthHeader()
      );
      toast.success(response.data.message || `Approval link sent to ${job.client_email}`);
      fetchJobs(); // Refresh to show email sent status
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingApprovalEmail(prev => ({ ...prev, [job.id]: false }));
    }
  };

  const handleArchiveJob = async (jobId, jobTitle) => {
    if (!window.confirm(`Archive "${jobTitle}"? This will move it to the Archives section.`)) {
      return;
    }
    
    try {
      await axios.put(`${API}/admin/jobs/${jobId}/archive`, {}, getAuthHeader());
      toast.success('Job archived successfully');
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive job');
    }
  };

  // Geocode addresses for canvassing/address_specific jobs (with background task polling)
  const handleGeocodeAddresses = async (jobId) => {
    setGeocodingAddresses(true);
    try {
      const response = await axios.post(
        `${API}/admin/jobs/${jobId}/geocode-addresses`,
        {},
        getAuthHeader()
      );
      
      if (response.data.status === 'processing' && response.data.task_id) {
        // Background task - poll for results
        toast.info(`Geocoding ${response.data.pending} addresses in background...`);
        const taskId = response.data.task_id;
        
        for (let i = 0; i < 600; i++) { // Poll up to 10 min
          await new Promise(r => setTimeout(r, 1000));
          try {
            const poll = await axios.get(`${API}/admin/jobs/geocoding-status/${taskId}`, getAuthHeader());
            const { status, total, geocoded, failed } = poll.data;
            
            // Update toast with progress
            if (i % 5 === 0) {
              toast.info(`Geocoding: ${geocoded + failed}/${total} processed...`, { id: 'geocoding-progress' });
            }
            
            if (status === 'complete') {
              toast.success(`Geocoded ${geocoded} of ${total} addresses${failed > 0 ? ` (${failed} failed)` : ''}`, { id: 'geocoding-progress' });
              // Re-fetch full job detail from DB to get updated addresses on map
              if (selectedJobDetail?.id === jobId) {
                try {
                  const freshJob = await axios.get(`${API}/admin/jobs/${jobId}`, getAuthHeader());
                  setSelectedJobDetail(freshJob.data);
                } catch (e) {
                  // Fallback: use addresses from polling response
                  if (poll.data.addresses) {
                    setSelectedJobDetail(prev => ({
                      ...prev,
                      specific_addresses: poll.data.addresses
                    }));
                  }
                }
              }
              fetchJobs();
              break;
            } else if (status === 'error') {
              toast.error(poll.data.error || 'Geocoding failed', { id: 'geocoding-progress' });
              break;
            }
          } catch (pollErr) {
            console.error('Poll error:', pollErr);
          }
        }
      } else {
        // Synchronous result
        const { geocoded, failed, total_addresses } = response.data;
        toast.success(`Geocoded ${geocoded} of ${total_addresses} addresses${failed > 0 ? ` (${failed} failed)` : ''}`);
        
        if (selectedJobDetail?.id === jobId && response.data.addresses) {
          setSelectedJobDetail(prev => ({
            ...prev,
            specific_addresses: response.data.addresses
          }));
        }
        fetchJobs();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to geocode addresses');
    } finally {
      setGeocodingAddresses(false);
    }
  };

  // Open approval modal for pending_review jobs
  const handleOpenApprovalModal = (job) => {
    setJobToApprove(job);
    setApprovalBonusPay(job.bonus_pay || 0);
    setShowApproveModal(true);
  };

  // Approve a pending_review job with optional bonus
  const handleApproveJob = async () => {
    if (!jobToApprove) return;
    
    setApprovingJob(true);
    try {
      await axios.put(`${API}/admin/jobs/${jobToApprove.id}/approve`, {
        bonus_pay: approvalBonusPay
      }, getAuthHeader());
      
      toast.success(`Job approved${approvalBonusPay > 0 ? ` with $${approvalBonusPay} bonus` : ''} and posted`);
      setShowApproveModal(false);
      setJobToApprove(null);
      setApprovalBonusPay(0);
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve job');
    } finally {
      setApprovingJob(false);
    }
  };

  // View job detail with map and delivery history
  const handleViewJobDetail = async (job) => {
    setSelectedJobDetail(job);
    setShowJobDetail(true);
    setLoadingJobDetail(true);
    setJobDeliveryHistory(null);
    setJobHouseLocations([]);
    setChildProgress(null);
    setTeamInfo(null);
    
    try {
      // Fire ALL independent requests in parallel for fast loading
      const [jobResponse, houseResponse, sessionsResponse, jobPhotosResponse, progressResponse] = await Promise.all([
        axios.get(`${API}/admin/jobs/${job.id}`, getAuthHeader()),
        axios.get(`${API}/admin/jobs/${job.id}/house-locations`, getAuthHeader()).catch(() => ({ data: { houses: [] } })),
        axios.get(`${API}/admin/sessions`, getAuthHeader()).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/jobs/${job.id}/photos`, getAuthHeader()).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/jobs/${job.id}/progress-details`, getAuthHeader()).catch(() => ({ data: null })),
      ]);
      
      // Set job details immediately
      setSelectedJobDetail(jobResponse.data);
      
      // Set house locations immediately (green dots appear fast)
      // For address_specific/canvassing jobs, NEVER use parcel cache — use specific_addresses
      const isAddrJob = ['address_specific', 'canvassing'].includes(jobResponse.data.delivery_type);
      if (isAddrJob) {
        // Address jobs use specific_addresses as markers — handled by map rendering
        // Do NOT load parcel data into jobHouseLocations
      } else if (houseResponse.data?.houses?.length > 0) {
        setJobHouseLocations(houseResponse.data.houses);
      } else if (jobResponse.data.delivery_boundary?.length >= 3 || jobResponse.data.delivery_polygons?.length > 0) {
        // No cache — auto-count houses in background using parcel DB (polygon jobs only)
        axios.post(`${API}/admin/jobs/${job.id}/refresh-house-cache`, { force: true }, getAuthHeader())
          .then(async (res) => {
            if (res.data?.count > 0) {
              try {
                const freshHouses = await axios.get(`${API}/admin/jobs/${job.id}/house-locations`, getAuthHeader());
                if (freshHouses.data?.houses?.length > 0) {
                  setJobHouseLocations(freshHouses.data.houses);
                }
              } catch { /* ignore */ }
            }
          })
          .catch(() => { /* background task, ignore errors */ });
      }
      
      // Process sessions and photos
      const jobSessions = (sessionsResponse.data || []).filter(s => s.job_id === job.id);
      const sessionsWithPhotos = await Promise.all(
        jobSessions.map(async (session) => {
          try {
            const photosResponse = await axios.get(`${API}/admin/sessions/${session.id}/photos`, getAuthHeader());
            return { ...session, photos: photosResponse.data };
          } catch {
            return { ...session, photos: [] };
          }
        })
      );
      
      const jobPhotos = jobPhotosResponse.data || [];
      const trackingProgress = progressResponse.data;
      
      // Fetch assigned user (depends on jobResponse)
      let assignedUser = null;
      if (jobResponse.data.assigned_user_id) {
        try {
          const userResponse = await axios.get(`${API}/admin/users/${jobResponse.data.assigned_user_id}`, getAuthHeader());
          assignedUser = userResponse.data;
        } catch {
          assignedUser = null;
        }
      }
      
      // Combine sessions with tracking progress data
      let allSessions = [...sessionsWithPhotos];
      
      // Use effective_deliveries from backend (reconciles all data sources)
      const effectiveDeliveries = trackingProgress?.effective_deliveries || 0;
      const isLiveTracking = trackingProgress?.live_tracking?.active === true;
      
      // If there's tracking progress data but no sessions, create a virtual session to show progress
      if (trackingProgress && effectiveDeliveries > 0 && allSessions.length === 0) {
        allSessions.push({
          id: 'tracking-progress',
          job_id: job.id,
          status: isLiveTracking ? 'in_progress' : 'completed',
          deliveries_count: effectiveDeliveries,
          distance: trackingProgress.tracking_progress.distance || 0,
          route_path: [],
          route_points: trackingProgress.tracking_progress.route_points || 0,
          created_at: trackingProgress.tracking_progress.updated_at,
          is_tracking_progress: true,
          is_live: isLiveTracking,
          admin_updated: trackingProgress.tracking_progress.admin_updated,
          admin_notes: trackingProgress.tracking_progress.admin_notes
        });
      }
      
      // If sessions exist but have 0 deliveries, patch in the effective count from tracking
      if (allSessions.length > 0 && effectiveDeliveries > 0) {
        const totalSessionDeliveries = allSessions.reduce((sum, s) => sum + (s.deliveries_count || 0), 0);
        if (totalSessionDeliveries === 0) {
          // Distribute the effective count to the most recent session
          allSessions[0].deliveries_count = effectiveDeliveries;
          allSessions[0].count_from_tracking = true;
        }
      }
      
      // Also check GPS history for route data
      if (trackingProgress && trackingProgress.gps_history?.route_points > 0) {
        // Update the first session or tracking progress with GPS info
        if (allSessions.length > 0) {
          allSessions[0].gps_route_points = trackingProgress.gps_history.route_points;
        }
      }
      
      setJobDeliveryHistory({
        sessions: allSessions,
        assignedUser,
        trackingProgress,
        jobPhotos
      });
      
      // Fetch child progress for parent jobs
      if (jobResponse.data.is_split_parent) {
        axios.get(`${API}/admin/jobs/${job.id}/child-progress`, getAuthHeader())
          .then(res => setChildProgress(res.data))
          .catch(() => setChildProgress(null));
      }
      
      // Fetch team info for team jobs
      if (jobResponse.data.is_team_job) {
        axios.get(`${API}/admin/jobs/${job.id}/team`, getAuthHeader())
          .then(res => setTeamInfo(res.data?.team))
          .catch(() => setTeamInfo(null));
      }
    } catch (error) {
      console.error('Error fetching job details:', error);
      toast.error('Failed to load job details');
    } finally {
      setLoadingJobDetail(false);
    }
  };

  const filteredJobs = jobs.filter(job =>
    job.title.toLowerCase().includes(search.toLowerCase()) ||
    (job.job_number && job.job_number.includes(search)) ||
    job.client.toLowerCase().includes(search.toLowerCase())
  );

  // Status counts (computed from search-filtered jobs)
  const statusCounts = filteredJobs.reduce((acc, job) => {
    acc[job.status] = (acc[job.status] || 0) + 1;
    return acc;
  }, {});

  const STATUS_FILTER_OPTIONS = [
    { value: 'all', label: 'All', color: 'bg-gray-100 text-gray-700 hover:bg-gray-200' },
    { value: 'in_progress', label: 'In Progress', color: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200' },
    { value: 'open', label: 'Open', color: 'bg-green-100 text-green-700 hover:bg-green-200' },
    { value: 'partial_complete', label: 'Partial', color: 'bg-orange-100 text-orange-700 hover:bg-orange-200' },
    { value: 'pending_review', label: 'Needs Payout', color: 'bg-red-100 text-red-700 hover:bg-red-200' },
    { value: 'pending_approval', label: 'Pending', color: 'bg-amber-100 text-amber-700 hover:bg-amber-200' },
    { value: 'expired', label: 'Expired', color: 'bg-rose-100 text-rose-700 hover:bg-rose-200' },
    { value: 'assigned', label: 'Assigned', color: 'bg-blue-100 text-blue-700 hover:bg-blue-200' },
    { value: 'completed', label: 'Completed', color: 'bg-purple-100 text-purple-700 hover:bg-purple-200' },
    { value: 'paid', label: 'Paid', color: 'bg-gray-100 text-gray-600 hover:bg-gray-200' },
    { value: 'parent', label: 'Parent', color: 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200' },
    { value: 'child', label: 'Child', color: 'bg-cyan-100 text-cyan-700 hover:bg-cyan-200' },
    { value: 'team', label: 'Team', color: 'bg-teal-100 text-teal-700 hover:bg-teal-200' },
    { value: 'overdue', label: 'Over Due', color: 'bg-red-100 text-red-800 hover:bg-red-200' },
  ];

  // Apply status filter
  // Status-or-expired matcher: jobs in pending_approval / open / assigned
  // with link_expired=True ALSO satisfy the 'expired' pseudo-filter so
  // they don't get hidden when admins expire a link in-flight.
  const isExpired = (job) => Boolean(job.link_expired) || job.status === 'expired';
  // Children: jobs with parent_job_id set (so they're a slice of a split
  // parent / grid / Remaining job). Excludes is_split_parent themselves.
  const isChild = (job) => Boolean(job.parent_job_id);
  // Over Due: any job (parent or child) whose deadline day has passed.
  // Finished jobs (completed/paid/archived/rejected) are excluded — an
  // already-delivered job past its date isn't actionable.
  const isOverdue = (job) => {
    if (!job.deadline) return false;
    if (['completed', 'paid', 'archived', 'rejected'].includes(job.status)) return false;
    const raw = String(job.deadline);
    const d = new Date(raw.includes('T') ? raw : `${raw}T23:59:59`);
    return !Number.isNaN(d.getTime()) && d.getTime() < Date.now();
  };
  const statusFilteredJobs = statusFilter === 'all'
    ? filteredJobs
    : statusFilter === 'parent'
    ? filteredJobs.filter(job => job.is_split_parent)
    : statusFilter === 'child'
    ? filteredJobs.filter(isChild)
    : statusFilter === 'team'
    ? filteredJobs.filter(job => job.is_team_job)
    : statusFilter === 'overdue'
    ? filteredJobs.filter(isOverdue)
    : statusFilter === 'expired'
    ? filteredJobs.filter(isExpired)
    : filteredJobs.filter(job => job.status === statusFilter);

  // Sort filtered jobs
  const STATUS_SORT_ORDER = {
    'in_progress': 0,
    'pending_review': 1,
    'partial_complete': 2,
    'assigned': 3,
    'open': 4,
    'pending_approval': 5,
    'completed': 6,
    'paid': 7,
    'rejected': 8,
    'archived': 9
  };

  const sortedJobs = [...statusFilteredJobs].sort((a, b) => {
    if (sortBy === 'default') return 0;
    if (sortBy === 'status') {
      return (STATUS_SORT_ORDER[a.status] ?? 99) - (STATUS_SORT_ORDER[b.status] ?? 99);
    }
    if (sortBy === 'newest') {
      return new Date(b.created_at) - new Date(a.created_at);
    }
    if (sortBy === 'oldest') {
      return new Date(a.created_at) - new Date(b.created_at);
    }
    return 0;
  });

  const handleMarkComplete = async (jobId, jobTitle) => {
    const silent = silentJobIds.has(jobId);
    if (!window.confirm(`Mark "${jobTitle}" as complete?${silent ? '\n\nNotify Silently is ON — no worker/customer notifications will be sent.' : ''}`)) return;
    try {
      await axios.put(`${API}/admin/jobs/${jobId}/mark-complete?silent=${silent}`, {}, getAuthHeader());
      toast.success(silent ? `"${jobTitle}" marked as complete silently (no notifications sent)` : `"${jobTitle}" marked as complete`);
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark job as complete');
    }
  };

  const handleUnmarkComplete = async (jobId, jobTitle) => {
    if (!window.confirm(`Revert "${jobTitle}" back to open?`)) return;
    try {
      await axios.put(`${API}/admin/jobs/${jobId}/unmark-complete`, {}, getAuthHeader());
      toast.success(`"${jobTitle}" reverted to open`);
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revert job');
    }
  };

  const handleToggleHidden = async (jobId, jobTitle, isCurrentlyHidden) => {
    try {
      await axios.put(`${API}/admin/jobs/${jobId}/toggle-hidden`, {}, getAuthHeader());
      toast.success(isCurrentlyHidden ? `"${jobTitle}" is now visible to users` : `"${jobTitle}" is now hidden from users`);
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update job visibility');
    }
  };

  // Bulk-publish every hidden child of a split/grid parent in one click.
  // Companion to the "auto-hide split children" rule shipped Feb 28, 2026 —
  // saves the admin from clicking Unhide on each of 20 cells individually.
  const handlePublishAllChildren = async (parentJob) => {
    if (!window.confirm(
      `Publish all hidden grid children for "${parentJob.title}"?\n\nThis makes every hidden child visible to workers in one click. Already-visible children are unchanged.`
    )) return;
    try {
      const res = await axios.post(
        `${API}/admin/jobs/${parentJob.id}/publish-children`,
        {},
        getAuthHeader(),
      );
      const data = res.data || {};
      if ((data.published_count || 0) > 0) {
        toast.success(data.message || `Published ${data.published_count} children`);
      } else {
        toast.info(data.message || 'No hidden children to publish');
      }
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish children');
    }
  };

  // Inverse of handlePublishAllChildren — pull every visible child off
  // the worker board in one click. Useful when a customer asks for a
  // re-draw or the admin needs to pause an entire grid.
  const handleHideAllChildren = async (parentJob) => {
    if (!window.confirm(
      `Hide all visible grid children for "${parentJob.title}"?\n\nThis removes every visible child from the worker job board in one click. Already-hidden children are unchanged.`
    )) return;
    try {
      const res = await axios.post(
        `${API}/admin/jobs/${parentJob.id}/hide-children`,
        {},
        getAuthHeader(),
      );
      const data = res.data || {};
      if ((data.hidden_count || 0) > 0) {
        toast.success(data.message || `Hid ${data.hidden_count} children`);
      } else {
        toast.info(data.message || 'No visible children to hide');
      }
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to hide children');
    }
  };

  const handleOpenRemoveUser = (job) => {
    setRemoveUserJob(job);
    setRemoveUserPostStatus('open');
    setShowRemoveUserModal(true);
  };

  const handleRemoveUser = async () => {
    if (!removeUserJob) return;
    setRemovingUser(true);
    try {
      const silent = silentJobIds.has(removeUserJob.id);
      const res = await axios.put(`${API}/admin/jobs/${removeUserJob.id}/remove-user`, {
        post_removal_status: removeUserPostStatus,
        silent
      }, getAuthHeader());
      toast.success(silent ? `${res.data.message} (silent — no notification sent)` : res.data.message);
      const jobToReassign = removeUserJob;
      setShowRemoveUserModal(false);
      setRemoveUserJob(null);
      fetchJobs();
      // "Assign to another user" — go straight into the Assign User modal
      // so the reassignment is completed in one flow.
      if (removeUserPostStatus === 'assigned') {
        handleOpenAssignUser({ ...jobToReassign, status: 'assigned', assigned_user_id: null, assigned_user_name: null });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove user');
    } finally {
      setRemovingUser(false);
    }
  };

  const handleOpenAssignUser = async (job) => {
    setAssignUserJob(job);
    setAssignSelectedUserId('');
    setAssignUserSearch('');
    setShowAssignUserModal(true);
    setLoadingUsers(true);
    try {
      const res = await axios.get(`${API}/admin/users`, getAuthHeader());
      setAssignUserList(res.data || []);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleAssignUser = async () => {
    if (!assignUserJob || !assignSelectedUserId) return;
    setAssigningUser(true);
    try {
      const silent = silentJobIds.has(assignUserJob.id);
      const res = await axios.put(`${API}/admin/jobs/${assignUserJob.id}/assign-user`, {
        user_id: assignSelectedUserId,
        silent
      }, getAuthHeader());
      toast.success(silent ? `${res.data.message} (silent — no notification sent)` : res.data.message);
      setShowAssignUserModal(false);
      setAssignUserJob(null);
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign user');
    } finally {
      setAssigningUser(false);
    }
  };

  const handleOpenTeamModal = async (job) => {
    setTeamModalJob(job);
    setShowTeamModal(true);
    setTeamModalLoading(true);
    setTeamUserSearch('');
    setTeamSelectedUsers([]);
    
    try {
      const [teamRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/jobs/${job.id}/team`, getAuthHeader()),
        axios.get(`${API}/admin/users`, getAuthHeader())
      ]);
      setTeamModalData(teamRes.data?.team);
      const approvedUsers = usersRes.data.filter(u => u.status === 'approved' && u.id !== job.assigned_user_id);
      setTeamAvailableUsers(approvedUsers);
    } catch (err) {
      toast.error('Failed to load team data');
    } finally {
      setTeamModalLoading(false);
    }
  };

  const handleAddTeamMembers = async () => {
    if (teamSelectedUsers.length === 0) return;
    try {
      await axios.post(`${API}/admin/jobs/${teamModalJob.id}/team`, { user_ids: teamSelectedUsers }, getAuthHeader());
      toast.success('Team members added');
      setTeamSelectedUsers([]);
      // Refresh team data
      const res = await axios.get(`${API}/admin/jobs/${teamModalJob.id}/team`, getAuthHeader());
      setTeamModalData(res.data?.team);
      fetchJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add team members');
    }
  };

  const handleRemoveTeamMember = async (userId, userName) => {
    if (!window.confirm(`Remove ${userName} from the team?`)) return;
    try {
      await axios.delete(`${API}/admin/jobs/${teamModalJob.id}/team/members/${userId}`, getAuthHeader());
      toast.success(`${userName} removed from team`);
      const res = await axios.get(`${API}/admin/jobs/${teamModalJob.id}/team`, getAuthHeader());
      setTeamModalData(res.data?.team);
      fetchJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove member');
    }
  };

  const handleDisbandTeam = async () => {
    if (!window.confirm('Disband this team? All members will be removed.')) return;
    try {
      await axios.delete(`${API}/admin/jobs/${teamModalJob.id}/team`, getAuthHeader());
      toast.success('Team disbanded');
      setTeamModalData(null);
      setShowTeamModal(false);
      fetchJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to disband team');
    }
  };

  // Reset to first page when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter]);

  // Pagination calculations
  const totalPages = Math.ceil(sortedJobs.length / JOBS_PER_PAGE);
  const startIndex = (currentPage - 1) * JOBS_PER_PAGE;
  const endIndex = startIndex + JOBS_PER_PAGE;
  const paginatedJobs = sortedJobs.slice(startIndex, endIndex);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending_approval':
        return <Badge className="bg-amber-100 text-amber-700 animate-pulse">Pending Approval</Badge>;
      case 'open':
        return <Badge className="bg-green-100 text-green-700">Open</Badge>;
      case 'assigned':
        return <Badge className="bg-blue-100 text-blue-700">Assigned</Badge>;
      case 'in_progress':
        return <Badge className="bg-yellow-100 text-yellow-700">In Progress</Badge>;
      case 'completed':
        return <Badge className="bg-purple-100 text-purple-700">Completed</Badge>;
      case 'partial_complete':
        return <Badge className="bg-orange-100 text-orange-700">Partial Complete</Badge>;
      case 'pending_review':
        return <Badge className="bg-red-100 text-red-700 animate-pulse">Needs Payout Approval</Badge>;
      case 'paid':
        return <Badge className="bg-gray-100 text-gray-700">Paid</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700">Rejected</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  return (
    <div className="p-6" data-testid="jobs-management-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Jobs Management</h1>
          <p className="text-sm text-gray-500">Manage, approve, and track delivery jobs</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Button
              variant="outline"
              onClick={() => setShowSortMenu(!showSortMenu)}
              className="flex items-center gap-2"
              data-testid="sort-jobs-btn"
            >
              <ArrowUpDown size={18} />
              Sort
            </Button>
            {showSortMenu && (
              <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-lg shadow-lg border z-50" data-testid="sort-menu">
                {[
                  { value: 'default', label: 'Default Order' },
                  { value: 'status', label: 'By Status' },
                  { value: 'newest', label: 'Newest First' },
                  { value: 'oldest', label: 'Oldest First' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => { setSortBy(opt.value); setShowSortMenu(false); setCurrentPage(1); }}
                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center justify-between ${sortBy === opt.value ? 'text-purple-700 font-medium bg-purple-50' : 'text-gray-700'}`}
                    data-testid={`sort-option-${opt.value}`}
                  >
                    {opt.label}
                    {sortBy === opt.value && <CheckSquare size={14} className="text-purple-600" />}
                  </button>
                ))}
              </div>
            )}
          </div>
          <Button
            variant="outline"
            onClick={fetchJobs}
            className="flex items-center gap-2"
          >
            <RefreshCw size={18} />
            Refresh
          </Button>
          {!isAuditor && (
            <Button
              variant="outline"
              onClick={async () => {
                if (!window.confirm('Set EVERY open, unassigned job to the standard $0.25 per house rate? Assigned and in-progress jobs are not touched.')) return;
                setApplyingRate(true);
                try {
                  const res = await axios.post(`${API}/admin/jobs/apply-standard-rate`, {}, getAuthHeader());
                  toast.success(`${res.data.updated} open job(s) updated to $0.25${res.data.already_at_rate ? ` — ${res.data.already_at_rate} already at rate` : ''}`);
                  fetchJobs();
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Failed to update open jobs');
                } finally {
                  setApplyingRate(false);
                }
              }}
              disabled={applyingRate}
              className="flex items-center gap-2 border-emerald-300 text-emerald-800 hover:bg-emerald-50"
              data-testid="apply-standard-rate-btn"
            >
              {applyingRate ? <Loader2 size={18} className="animate-spin" /> : <DollarSign size={18} />}
              Set open jobs to $0.25
            </Button>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input
          placeholder="Search jobs..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Status Filter Bar */}
      <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="status-filter-bar">
        {STATUS_FILTER_OPTIONS.map(opt => {
          const count = opt.value === 'all'
            ? filteredJobs.length
            : opt.value === 'parent'
            ? filteredJobs.filter(j => j.is_split_parent).length
            : opt.value === 'child'
            ? filteredJobs.filter(isChild).length
            : opt.value === 'team'
            ? filteredJobs.filter(j => j.is_team_job).length
            : opt.value === 'overdue'
            ? filteredJobs.filter(isOverdue).length
            : opt.value === 'expired'
            ? filteredJobs.filter(isExpired).length
            : (statusCounts[opt.value] || 0);
          if (opt.value !== 'all' && count === 0) return null;
          const isActive = statusFilter === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => setStatusFilter(opt.value)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                isActive
                  ? `${opt.color} ring-2 ring-offset-1 ring-purple-400 shadow-sm`
                  : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
              }`}
              data-testid={`status-filter-${opt.value}`}
            >
              {opt.label} <span className={`ml-1 ${isActive ? 'opacity-100' : 'opacity-60'}`}>({count})</span>
            </button>
          );
        })}
      </div>

      {/* Jobs List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full"></div>
        </div>
      ) : sortedJobs.length === 0 ? (
        <Card className="p-12 text-center border-0 shadow-sm">
          <Briefcase size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500 mb-4">No jobs found</p>
          {!isAuditor && (
            <Button
              className="bg-purple-600"
              onClick={() => setShowCreateModal(true)}
            >
              <Plus size={18} className="mr-2" />
              Create First Job
            </Button>
          )}
        </Card>
      ) : (
        <>
        {/* Bulk Action Bar */}
        {!isAuditor && selectedJobs.size > 0 && (
          <div className="mb-4 flex items-center justify-between bg-purple-50 border border-purple-200 rounded-xl px-4 py-3" data-testid="bulk-action-bar">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={paginatedJobs.length > 0 && paginatedJobs.every(j => selectedJobs.has(j.id))}
                onChange={toggleSelectAll}
                className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                data-testid="select-all-checkbox"
              />
              <span className="text-sm font-medium text-purple-800">{selectedJobs.size} job{selectedJobs.size > 1 ? 's' : ''} selected</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedJobs(new Set())}
                data-testid="clear-selection-btn"
              >
                Clear
              </Button>
              <Button
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={handleBulkMarkComplete}
                disabled={bulkCompleting || bulkDeleting}
                data-testid="bulk-mark-complete-btn"
              >
                {bulkCompleting ? (
                  <><div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" /> Completing...</>
                ) : (
                  <><CheckCircle size={14} className="mr-1" /> Mark Complete {selectedJobs.size}</>
                )}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleBulkDelete}
                disabled={bulkDeleting || bulkCompleting}
                data-testid="bulk-delete-btn"
              >
                {bulkDeleting ? (
                  <><div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" /> Deleting...</>
                ) : (
                  <><Trash2 size={14} className="mr-1" /> Delete {selectedJobs.size}</>
                )}
              </Button>
            </div>
          </div>
        )}
        <div className="space-y-4">
          {paginatedJobs.map((job) => (
            <Card 
              key={job.id} 
              className={`p-4 border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer ${selectedJobs.has(job.id) ? 'ring-2 ring-purple-400 bg-purple-50/50' : ''}`}
              onClick={() => handleViewJobDetail(job)}
              data-testid={`job-card-${job.id}`}
            >
              {/* Large red warning when a low-rated user (3★ or less) is requesting this job */}
              {job.requested_by && (() => {
                const ratings = [job.requester_audit_rating, job.requester_rating].filter((r) => r != null);
                if (!ratings.length) return null;
                const lowest = Math.min(...ratings);
                if (lowest > 3) return null;
                return (
                  <div
                    className="mb-3 flex items-center gap-3 rounded-lg bg-red-600 text-white px-4 py-3 shadow-md border-2 border-red-700"
                    data-testid={`low-rating-warning-${job.id}`}
                  >
                    <AlertTriangle size={30} className="shrink-0 text-yellow-300" />
                    <div>
                      <p className="text-lg font-extrabold uppercase tracking-wide">Low Rating User</p>
                      <p className="text-sm text-red-100">
                        {job.requester_name || 'This user'} is requesting this job with a {Number(lowest).toFixed(1)}★ rating (3★ or less). Review carefully before approving.
                      </p>
                    </div>
                  </div>
                );
              })()}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-3">
                    {!isAuditor && (
                      <input
                        type="checkbox"
                        checked={selectedJobs.has(job.id)}
                        onChange={(e) => { e.stopPropagation(); toggleJobSelection(job.id); }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        data-testid={`job-checkbox-${job.id}`}
                      />
                    )}
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center">
                      <Briefcase size={24} className="text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {job.job_number && <span className="text-blue-600 mr-1">#{job.job_number}</span>}
                      {job.title}
                      <span className="ml-2 text-xs font-mono text-gray-400" title={job.id}>{job.id.slice(0, 8)}</span>
                    </h3>
                    <p className="text-sm text-gray-500">{job.client} • {job.location}</p>
                    {/* Show assigned user for in-progress/assigned jobs */}
                    {job.assigned_user_name && (job.status === 'in_progress' || job.status === 'assigned') && (
                      <p className="text-xs text-emerald-600 font-medium flex items-center gap-1 mt-0.5">
                        <User size={12} />
                        Working: {job.assigned_user_name}
                      </p>
                    )}
                    {/* Show bonus pay if present */}
                    {job.bonus_pay > 0 && (
                      <p className="text-xs text-yellow-600 font-medium flex items-center gap-1">
                        ★ Bonus: ${job.bonus_pay.toFixed(2)}
                      </p>
                    )}
                    {/* Show child job label */}
                    {job.parent_job_id && (
                      <p className="text-xs text-blue-600 font-medium">Child Job</p>
                    )}
                    {/* Show parent job label */}
                    {job.is_split_parent && (
                      <p className="text-xs text-indigo-600 font-medium">Parent Job</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-end max-w-full" onClick={(e) => e.stopPropagation()} data-testid={`job-actions-${job.id}`}>
                  {getStatusBadge(job.status)}
                  
                  {/* Parent Job Badge */}
                  {job.is_split_parent && (
                    <Badge className="bg-indigo-100 text-indigo-700 border border-indigo-300">
                      <Grid3X3 size={12} className="mr-1" />
                      Parent Job
                    </Badge>
                  )}
                  
                  {/* Child Job Badge */}
                  {job.parent_job_id && (
                    <Badge className="bg-sky-100 text-sky-700 border border-sky-300">
                      Child
                    </Badge>
                  )}
                  
                  {/* Team Job Badge */}
                  {job.is_team_job && (
                    <Badge className="bg-teal-100 text-teal-700 border border-teal-300">
                      <Users size={12} className="mr-1" />
                      Team
                    </Badge>
                  )}

                  {/* Team submission progress chip — shows how many
                      teammates have submitted their session vs the
                      total roster (originator + accepted members).
                      Helps the dispatcher see at a glance which team
                      job is still waiting on a laggard before it
                      reaches the auditor. Only renders for in-flight
                      team jobs so completed jobs don't get a stale
                      "submitted" chip. */}
                  {job.is_team_job
                    && job.status !== 'completed'
                    && job.status !== 'archived'
                    && (() => {
                        const roster = new Set(job.team_member_ids || []);
                        if (job.assigned_user_id) roster.add(job.assigned_user_id);
                        const total = roster.size;
                        const submitted = (job.team_submitted_user_ids || [])
                          .filter(uid => roster.has(uid)).length;
                        if (total === 0) return null;
                        const allIn = submitted >= total;
                        return (
                          <Badge
                            className={
                              allIn
                                ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                                : submitted > 0
                                  ? 'bg-amber-100 text-amber-700 border border-amber-300'
                                  : 'bg-slate-100 text-slate-600 border border-slate-300'
                            }
                            data-testid={`team-submitted-chip-${job.id}`}
                            title={
                              allIn
                                ? 'All teammates have submitted their session'
                                : `${total - submitted} teammate(s) still tracking on their device`
                            }
                          >
                            {submitted}/{total} submitted
                          </Badge>
                        );
                      })()}
                  
                  {/* Hidden Badge */}
                  {job.is_hidden && (
                    <Badge className="bg-red-100 text-red-700 border border-red-300" data-testid={`hidden-badge-${job.id}`}>
                      <EyeOff size={12} className="mr-1" />
                      Hidden
                    </Badge>
                  )}
                  
                  {/* Dual Approval Status for pending_approval jobs */}
                  {job.status === 'pending_approval' && (
                    <div className="flex items-center gap-1">
                      <Badge className={job.admin_approved ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                        {job.admin_approved ? '✓ Admin' : '⏳ Admin'}
                      </Badge>
                      <Badge className={job.client_approved ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                        {job.client_approved ? '✓ Client' : '⏳ Client'}
                      </Badge>
                    </div>
                  )}
                  
                  {/* Client Approved Badge for other statuses */}
                  {job.status !== 'pending_approval' && job.client_approved && (
                    <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300">
                      <CheckCircle size={12} className="mr-1" />
                      Client Approved
                    </Badge>
                  )}
                  
                  {/* Reactivate (un-expire) button — shows on any expired
                      job. Backend preserves whichever side already approved
                      (e.g. Client Approved badge remains visible after
                      reactivation). Admin / Manager only — auditors blocked
                      because the backend endpoint uses require_manager_or_admin. */}
                  {!isAuditor && job.status === 'expired' && canManageJobs && (
                    <Button
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); handleReactivateJob(job); }}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                      data-testid={`reactivate-job-${job.id}`}
                      title="Un-expire this job and start a new 7-day approval window"
                    >
                      <RefreshCw size={16} className="mr-1" />
                      Reactivate
                    </Button>
                  )}
                  
                  {/* Approve/Reject buttons for pending_approval jobs */}
                  {!isAuditor && job.status === 'pending_approval' && !job.admin_approved && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => handleApprovePendingJob(job.id, job.title)}
                        className="bg-green-600 hover:bg-green-700 text-white"
                        data-testid={`approve-job-${job.id}`}
                      >
                        <CheckCircle size={16} className="mr-1" />
                        Approve
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRejectPendingJob(job.id, job.title)}
                        className="text-red-600 border-red-300 hover:bg-red-50"
                        data-testid={`reject-job-${job.id}`}
                      >
                        <X size={16} className="mr-1" />
                        Reject
                      </Button>
                    </>
                  )}

                  {/* Re-approve button for previously rejected jobs.
                      Mirrors the approve flow above but uses an explicit
                      confirmation since the action reverses a deliberate
                      rejection decision. Triggers staff bell notifications
                      + a client email server-side. */}
                  {!isAuditor && job.status === 'rejected' && (
                    <Button
                      size="sm"
                      onClick={() => handleUnrejectJob(job.id, job.title)}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid={`unreject-job-${job.id}`}
                      title="Change the status back to approved — notifies the client + all staff"
                    >
                      <CheckCircle size={16} className="mr-1" />
                      Re-approve
                    </Button>
                  )}
                  
                  {/* User Request Badge and Approval Buttons */}
                  {job.requested_by && (
                    <div className="flex items-center gap-2">
                      {(() => {
                        const waitHrs = job.requested_at ? (Date.now() - new Date(job.requested_at).getTime()) / 36e5 : 0;
                        const overdue = waitHrs > 24;
                        return (
                          <Badge
                            className={`${overdue ? 'bg-red-600 border-red-800' : 'bg-blue-600 border-blue-800'} text-white border-2 px-3 py-1.5 text-sm font-bold shadow-lg animate-pulse ${overdue ? 'hover:bg-red-600' : 'hover:bg-blue-600'}`}
                            title={overdue ? `Requested ${Math.floor(waitHrs / 24)}d ${Math.round(waitHrs % 24)}h ago — don't leave them hanging!` : 'Pending job request'}
                            data-testid={`user-requested-badge-${job.id}`}
                          >
                            <UserPlus size={15} className="mr-1.5" />
                            {job.requester_name || 'User'} Requested
                            {overdue && ` · ${Math.floor(waitHrs / 24) > 0 ? `${Math.floor(waitHrs / 24)}d` : ''}${Math.round(waitHrs % 24)}h`}
                          </Badge>
                        );
                      })()}
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleApproveUserRequest(job.id, job.title);
                        }}
                        className="bg-green-600 hover:bg-green-700 text-white"
                        data-testid={`approve-request-${job.id}`}
                      >
                        <CheckCircle size={16} className="mr-1" />
                        Approve User
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDenyUserRequest(job.id, job.title);
                        }}
                        className="text-red-600 border-red-300 hover:bg-red-50"
                        data-testid={`deny-request-${job.id}`}
                      >
                        <X size={16} className="mr-1" />
                        Deny
                      </Button>
                    </div>
                  )}
                  
                  {/* Client Approval Link button */}
                  {!isAuditor && job.status === 'pending_approval' && job.client_approval_token && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const url = `${window.location.origin}/client/approve/${job.client_approval_token}`;
                          navigator.clipboard.writeText(url);
                          toast.success('Client approval link copied!');
                        }}
                        className="text-blue-600 border-blue-300 hover:bg-blue-50"
                        title="Copy client approval link"
                        data-testid={`client-approval-link-${job.id}`}
                      >
                        <Link2 size={16} className="mr-1" />
                        Client Link
                      </Button>
                      {job.client_email && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSendApprovalEmail(job)}
                          disabled={sendingApprovalEmail[job.id] || job.client_approved}
                          className={`${job.client_approval_email_sent 
                            ? 'text-green-600 border-green-300 hover:bg-green-50' 
                            : 'text-purple-600 border-purple-300 hover:bg-purple-50'}`}
                          title={job.client_approval_email_sent 
                            ? 'Email already sent - click to resend' 
                            : 'Send approval link via email'}
                          data-testid={`send-approval-email-${job.id}`}
                        >
                          {sendingApprovalEmail[job.id] ? (
                            <Loader2 size={16} className="mr-1 animate-spin" />
                          ) : (
                            <Send size={16} className="mr-1" />
                          )}
                          {job.client_approval_email_sent ? 'Resend' : 'Send Email'}
                        </Button>
                      )}
                    </>
                  )}
                  {/* Team Management Button for in-progress jobs.
                      Visible to admins AND managers — backend permits both. */}
                  {canManageTeams && job.status === 'in_progress' && job.assigned_user_id && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); handleOpenTeamModal(job); }}
                      className={`${job.is_team_job ? 'text-teal-600 border-teal-300 hover:bg-teal-50' : 'text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                      data-testid={`team-btn-${job.id}`}
                    >
                      <Users size={16} className="mr-1" />
                      {job.is_team_job ? 'Manage Team' : 'Form Team'}
                    </Button>
                  )}
                  {/* Assign / Re-assign User button — visible on any job in
                      an open-ish status (open / pending_approval / approved),
                      plus jobs left in status "assigned" with NO user (the
                      remove-user "assign to another user" path) so they can
                      never get stuck showing Assigned with nobody on it. */}
                  {!isAuditor && canManageJobs && (['open', 'pending_approval', 'approved'].includes(job.status) || (job.status === 'assigned' && !job.assigned_user_id)) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); handleOpenAssignUser(job); }}
                      className="text-blue-600 border-blue-300 hover:bg-blue-50"
                      data-testid={`assign-user-${job.id}`}
                      title={job.assigned_user_id
                        ? `Re-assign this re-opened job (was: ${job.assigned_user_name || 'previous user'})`
                        : 'Assign a user to this job'}
                    >
                      <UserPlus size={16} className="mr-1" />
                      {job.assigned_user_id ? 'Re-assign User' : 'Assign User'}
                    </Button>
                  )}
                  {/* Hide/Unhide Button — manager and admin */}
                  {!isAuditor && canManageJobs && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); handleToggleHidden(job.id, job.title, job.is_hidden); }}
                      className={job.is_hidden ? 'text-red-600 border-red-300 hover:bg-red-50' : 'text-gray-500 border-gray-300 hover:bg-gray-50'}
                      data-testid={`toggle-hidden-${job.id}`}
                      title={job.is_hidden ? 'Unhide job (make visible to users)' : 'Hide job from users'}
                    >
                      {job.is_hidden ? <Eye size={16} className="mr-1" /> : <EyeOff size={16} className="mr-1" />}
                      {job.is_hidden ? 'Unhide' : 'Hide'}
                    </Button>
                  )}
                  {/* Remove User Button — only on in_progress/assigned jobs with an assigned user */}
                  {!isAuditor && canManageJobs && ['in_progress', 'assigned'].includes(job.status) && job.assigned_user_id && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); handleOpenRemoveUser(job); }}
                      className="text-rose-600 border-rose-300 hover:bg-rose-50"
                      data-testid={`remove-user-${job.id}`}
                      title="Remove assigned user from this job"
                    >
                      <UserX size={16} className="mr-1" />
                      Remove User
                    </Button>
                  )}
                  {/* Mark Complete Button — manager and admin */}
                  {!isAuditor && canManageJobs && !['completed', 'archived', 'paid', 'pending_approval'].includes(job.status) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleMarkComplete(job.id, job.title)}
                      className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                      data-testid={`mark-complete-${job.id}`}
                    >
                      <CheckSquare size={16} className="mr-1" />
                      Mark Complete
                    </Button>
                  )}
                  {/* Archive Button for completed jobs */}
                  {!isAuditor && job.status === 'completed' && (
                    <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleUnmarkComplete(job.id, job.title)}
                      className="text-amber-600 border-amber-300 hover:bg-amber-50"
                      data-testid={`unmark-complete-${job.id}`}
                    >
                      <XCircle size={16} className="mr-1" />
                      Reopen Job
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleArchiveJob(job.id, job.title)}
                      className="text-gray-600 border-gray-300 hover:bg-gray-50"
                      data-testid={`archive-job-${job.id}`}
                    >
                      <Archive size={16} className="mr-1" />
                      Archive
                    </Button>
                    </>
                  )}
                  {/* Payout Approval Button for pending_review jobs */}
                  {!isAuditor && job.status === 'pending_review' && (
                    <Button
                      size="sm"
                      onClick={() => handleOpenApprovalModal(job)}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid={`approve-payout-${job.id}`}
                    >
                      <DollarSign size={16} className="mr-1" />
                      Approve Payout
                    </Button>
                  )}
                  {/* Zone Preview Link */}
                  {job.delivery_zone_id && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyJobZonePreviewLink(job)}
                      className="text-purple-600 border-purple-300 hover:bg-purple-50"
                      title="Copy zone preview link (for client review)"
                      data-testid={`zone-preview-link-${job.id}`}
                    >
                      <MapPin size={16} className="mr-1" />
                      Zone
                    </Button>
                  )}
                  {/* Customer Tracking Link — opens the public tracking page in a new tab */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyShareableLink(job.id)}
                    className="text-green-600 border-green-300 hover:bg-green-50"
                    title="Open customer tracking page in a new tab"
                    data-testid={`copy-link-${job.id}`}
                  >
                    <Link2 size={16} className="mr-1" />
                    Track
                  </Button>
                  {/* Admin Mark — opens the dedicated admin marking page where
                      the admin/auditor can drop purple house markers and
                      credit them to a chosen contributor. Visible to all
                      staff roles. */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/admin/jobs/${job.id}/mark`)}
                    className="text-purple-600 border-purple-300 hover:bg-purple-50"
                    title="Open Admin Mark — manually mark delivered houses on the map"
                    data-testid={`admin-mark-${job.id}`}
                  >
                    <MapPin size={16} className="mr-1" />
                    Admin Mark
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewJobDetail(job)}
                    data-testid={`view-job-${job.id}`}
                  >
                    <Eye size={16} className="mr-1" />
                    View
                  </Button>
                  {isFullAdmin && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditJob(job)}
                      data-testid={`edit-job-${job.id}`}
                    >
                      <Edit size={16} className="mr-1" />
                      Edit
                    </Button>
                  )}
                  {canManageJobs && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                      onClick={() => handleCloneJob(job)}
                      data-testid={`clone-job-${job.id}`}
                    >
                      <Copy size={16} className="mr-1" />
                      Clone
                    </Button>
                  )}
                  {canSplitJobs && _canSplit(job) && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                        onClick={() => handleSplitIntoGridJobs(job)}
                        disabled={splittingIntoGrids}
                        title="Auto-split this job into grid sections"
                        data-testid={`split-job-${job.id}`}
                      >
                        <Grid3X3 size={16} className="mr-1" />
                        Split
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                        onClick={() => { setSplitModalJob(job); setCustomSplitCount(Math.max(2, Math.ceil((job.deliveries || 100) / 450))); }}
                        disabled={splittingIntoGrids}
                        title="Choose how many grid sections to split into"
                        data-testid={`split-custom-job-${job.id}`}
                      >
                        <Settings size={16} className="mr-1" />
                        Custom...
                      </Button>
                    </>
                  )}
                  {canManageJobs && job.is_split_parent && (job.child_count || 0) > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 border-emerald-300"
                      onClick={(e) => { e.stopPropagation(); handlePublishAllChildren(job); }}
                      title="Make every hidden grid child visible to workers in one click"
                      data-testid={`publish-children-${job.id}`}
                    >
                      <Eye size={16} className="mr-1" />
                      Publish All Children
                    </Button>
                  )}
                  {canManageJobs && job.is_split_parent && (job.child_count || 0) > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-300"
                      onClick={(e) => { e.stopPropagation(); handleHideAllChildren(job); }}
                      title="Pull every visible grid child off the worker board in one click"
                      data-testid={`hide-children-${job.id}`}
                    >
                      <EyeOff size={16} className="mr-1" />
                      Hide All Children
                    </Button>
                  )}
                  {canSplitJobs && job.is_split_parent && (job.child_count || 0) === 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 border-amber-300"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSplitModalJob(job);
                        setCustomSplitCount(Math.max(2, Math.ceil((job.deliveries || 100) / 450)));
                      }}
                      disabled={splittingIntoGrids}
                      title="All child jobs were deleted — re-create them from this parent's zone"
                      data-testid={`regrid-job-${job.id}`}
                    >
                      <Grid3X3 size={16} className="mr-1" />
                      Re-grid
                    </Button>
                  )}
                  {isFullAdmin && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDeleteJob(job.id, job.title)}
                      data-testid={`delete-job-${job.id}`}
                    >
                      <Trash2 size={16} className="mr-1" />
                      Delete
                    </Button>
                  )}
                  {canManageJobs && (job.delivery_boundary?.length >= 3 || job.delivery_polygons?.length > 0) && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-green-600 hover:text-green-700 hover:bg-green-50"
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          toast.info(`Re-counting houses for "${job.title}"...`, { duration: 3000 });
                          const result = await axios.post(`${API}/admin/jobs/${job.id}/refresh-house-cache`, { force: true }, getAuthHeader());
                          const count = result.data?.count || 0;
                          if (result.data?.deliveries_updated) {
                            toast.success(`${job.title}: ${result.data.old_deliveries} → ${result.data.new_deliveries} houses. Pay updated.`, { duration: 5000 });
                          } else {
                            toast.success(`${job.title}: ${count} houses confirmed.`);
                          }
                          // Update detail panel if viewing this job
                          if (selectedJobDetail?.id === job.id) {
                            setSelectedJobDetail(prev => ({
                              ...prev,
                              deliveries: result.data?.new_deliveries ?? count,
                              total_pay: result.data?.new_total_pay ?? prev.total_pay
                            }));
                          }
                          fetchJobs();
                        } catch (err) {
                          toast.error(`Failed to re-count: ${err.message}`);
                        }
                      }}
                      data-testid={`recount-job-${job.id}`}
                    >
                      <RefreshCw size={16} className="mr-1" />
                      Re-count
                    </Button>
                  )}
                  {/* Notify Silently — per-job checkbox. When checked, the
                      notifying actions on this card (Assign User, Remove
                      User, Mark Complete) send no worker/customer
                      notifications or emails. */}
                  {!isAuditor && canManageJobs && (
                    <label
                      className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none ml-1"
                      title="When checked, Assign User, Remove User and Mark Complete send no notifications to workers or the customer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={silentJobIds.has(job.id)}
                        onChange={() => toggleJobSilent(job.id)}
                        className="w-3.5 h-3.5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                        data-testid={`notify-silently-checkbox-${job.id}`}
                      />
                      Notify Silently
                    </label>
                  )}
                </div>
              </div>
              
              {/* Approval Info */}
              {job.approved_by_name && (
                <div className="px-4 py-2 bg-green-50 border-t border-green-100 text-xs text-green-700">
                  <CheckCircle size={12} className="inline mr-1" />
                  Approved by {job.approved_by_name} on {new Date(job.approved_at).toLocaleDateString()}
                </div>
              )}
              {job.rejected_by_name && (
                <div className="px-4 py-2 bg-red-50 border-t border-red-100 text-xs text-red-700">
                  <X size={12} className="inline mr-1" />
                  Rejected by {job.rejected_by_name} on {new Date(job.rejected_at).toLocaleDateString()}
                </div>
              )}
              
              {/* Client Contact Info */}
              {(job.client_contact_name || job.client_phone || job.client_email) && (
                <div className="flex items-center gap-4 mt-3 pt-3 border-t border-dashed">
                  {job.client_contact_name && (
                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                      <User size={14} className="text-purple-500" />
                      <span>{job.client_contact_name}</span>
                    </div>
                  )}
                  {job.client_phone && (
                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                      <Phone size={14} className="text-green-500" />
                      <span>{job.client_phone}</span>
                    </div>
                  )}
                  {job.client_email && (
                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                      <Mail size={14} className="text-blue-500" />
                      <span>{job.client_email}</span>
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex items-center gap-6 mt-3 pt-3 border-t">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Package size={16} className="text-orange-500" />
                  <span>{job.deliveries} flyers</span>
                </div>
                {!isAuditor && (
                  <div className="flex items-center gap-2 text-sm font-medium text-green-600">
                    <DollarSign size={16} />
                    <span>${job.total_pay.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Calendar size={16} className="text-blue-500" />
                  <span>Due: {job.deadline}</span>
                </div>
              </div>

              {/* Workers row — bold clickable names of every user
                  attached to this job. Originator first (badge: "Lead"),
                  then each team member. On team jobs each name carries
                  a ✓ when they've submitted their session,
                  ⏳ when they're still tracking. Clicking a name opens
                  the message dialog so an auditor/admin can ping the
                  worker (e.g. "Please remember to End Session before
                  you head home"). Hidden when no one is attached. */}
              {(() => {
                const workers = [];
                const seen = new Set();
                if (job.assigned_user_id && !seen.has(job.assigned_user_id)) {
                  seen.add(job.assigned_user_id);
                  workers.push({ id: job.assigned_user_id, role: 'lead' });
                }
                (job.team_member_ids || []).forEach(uid => {
                  if (uid && !seen.has(uid)) {
                    seen.add(uid);
                    workers.push({ id: uid, role: 'member' });
                  }
                });
                if (workers.length === 0) return null;
                const submittedSet = new Set(job.team_submitted_user_ids || []);
                const isTeam = !!job.is_team_job;
                return (
                  <div
                    className="flex items-center flex-wrap gap-2 mt-3 pt-3 border-t"
                    data-testid={`workers-row-${job.id}`}
                  >
                    <Users size={14} className="text-gray-400" />
                    <span className="text-xs uppercase tracking-wide text-gray-500 mr-1">
                      Workers:
                    </span>
                    {workers.map(w => {
                      const u = userMap[w.id];
                      const displayName = u?.name || w.id.slice(0, 8);
                      const submitted = submittedSet.has(w.id);
                      return (
                        <button
                          key={w.id}
                          type="button"
                          onClick={(e) => {
                            // Card has its own click handlers (View
                            // Details modal). Stop propagation so the
                            // message modal opens on its own without
                            // also opening the job-detail modal underneath.
                            e.stopPropagation();
                            setMessageWorker({
                              user_id: w.id,
                              name: displayName,
                              job_title: job.title,
                              email: u?.email || '',
                            });
                            setMessageBody('');
                          }}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-bold text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 transition-colors"
                          data-testid={`message-worker-${job.id}-${w.id}`}
                          title={`Send a message to ${displayName}${u?.email ? ` (${u.email})` : ''}`}
                        >
                          {isTeam && (
                            <span
                              className={submitted ? 'text-emerald-600' : 'text-amber-500'}
                              aria-label={submitted ? 'submitted' : 'still tracking'}
                            >
                              {submitted ? '✓' : '⏳'}
                            </span>
                          )}
                          <span>{displayName}</span>
                          {w.role === 'lead' && (
                            <span className="ml-1 text-[10px] uppercase tracking-wide font-semibold text-purple-500">
                              Lead
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
            </Card>
          ))}
        </div>
        
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-6 border-t">
            <div className="text-sm text-gray-600">
              Showing {startIndex + 1}-{Math.min(endIndex, filteredJobs.length)} of {filteredJobs.length} jobs
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className="flex items-center gap-1"
              >
                <ChevronLeft size={16} />
                Previous
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <Button
                      key={pageNum}
                      variant={currentPage === pageNum ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        setCurrentPage(pageNum);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className={currentPage === pageNum ? "bg-purple-600" : ""}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className="flex items-center gap-1"
              >
                Next
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        )}
        </>
      )}

      {/* Edit Job Modal */}
      <Dialog open={showEditModal} onOpenChange={(open) => {
        setShowEditModal(open);
        if (!open) setEditingJob(null);
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Job Details</DialogTitle>
          </DialogHeader>
          {editingJob && (
            <form onSubmit={handleUpdateJob} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Job Title *</Label>
                  <Input
                    value={editingJob.title}
                    onChange={(e) => setEditingJob({ ...editingJob, title: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Client Name *</Label>
                  <Input
                    value={editingJob.client}
                    onChange={(e) => setEditingJob({ ...editingJob, client: e.target.value })}
                    required
                  />
                </div>
              </div>

              {/* Client Contact Information */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg">
                <div>
                  <Label className="flex items-center gap-1">
                    <User size={14} className="text-gray-500" />
                    Contact Name
                  </Label>
                  <Input
                    value={editingJob.client_contact_name}
                    onChange={(e) => setEditingJob({ ...editingJob, client_contact_name: e.target.value })}
                    placeholder="e.g., John Smith"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-1">
                    <Phone size={14} className="text-gray-500" />
                    Contact Phone
                  </Label>
                  <Input
                    value={editingJob.client_phone}
                    onChange={(e) => setEditingJob({ ...editingJob, client_phone: e.target.value })}
                    placeholder="e.g., (555) 123-4567"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-1">
                    <Mail size={14} className="text-gray-500" />
                    Contact Email
                  </Label>
                  <Input
                    type="email"
                    value={editingJob.client_email}
                    onChange={(e) => setEditingJob({ ...editingJob, client_email: e.target.value })}
                    placeholder="e.g., john@example.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Location *</Label>
                  <Input
                    value={editingJob.location}
                    onChange={(e) => setEditingJob({ ...editingJob, location: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Flyer Type *</Label>
                  <Input
                    value={editingJob.flyer_type}
                    onChange={(e) => setEditingJob({ ...editingJob, flyer_type: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Number of Deliveries *</Label>
                  <Input
                    type="number"
                    value={editingJob.deliveries}
                    onChange={(e) => setEditingJob({ ...editingJob, deliveries: parseInt(e.target.value) })}
                    min="1"
                    required
                  />
                </div>
                <div>
                  <Label>Pay Per Flyer ($) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingJob.pay_per_flyer}
                    onChange={(e) => setEditingJob({ ...editingJob, pay_per_flyer: parseFloat(e.target.value) })}
                    min="0.01"
                    required
                  />
                </div>
                <div>
                  <Label>Deadline *</Label>
                  <Input
                    type="date"
                    value={editingJob.deadline}
                    onChange={(e) => setEditingJob({ ...editingJob, deadline: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <Label>Status</Label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-gray-200 bg-white text-sm"
                  value={editingJob.status}
                  onChange={(e) => setEditingJob({ ...editingJob, status: e.target.value })}
                >
                  <option value="open">Open</option>
                  <option value="assigned">Assigned</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="paid">Paid</option>
                </select>
              </div>

              <div>
                <Label>Description</Label>
                <Input
                  value={editingJob.description || ''}
                  onChange={(e) => setEditingJob({ ...editingJob, description: e.target.value })}
                  placeholder="Additional details..."
                />
              </div>

              {/* Total Pay Preview */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-700 font-medium">Total Job Pay</span>
                  <span className="text-2xl font-bold text-green-600">
                    ${(editingJob.deliveries * editingJob.pay_per_flyer).toFixed(2)}
                  </span>
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <Button type="submit" className="flex-1 bg-purple-600 hover:bg-purple-700">
                  Save Changes
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Job Detail Modal */}
      <Dialog open={showJobDetail} onOpenChange={(open) => {
        setShowJobDetail(open);
        if (!open) {
          setSelectedJobDetail(null);
          setJobDeliveryHistory(null);
        }
      }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye size={20} className="text-purple-600" />
              Job Details
            </DialogTitle>
          </DialogHeader>
          
          {loadingJobDetail ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
            </div>
          ) : selectedJobDetail && (
            <div className="space-y-6">
              {/* Job Header */}
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-xl font-bold text-gray-900">
                    {selectedJobDetail.job_number && <span className="text-blue-600 mr-1">#{selectedJobDetail.job_number}</span>}
                    {selectedJobDetail.title}
                  </h2>
                  <p className="text-gray-600">{selectedJobDetail.client} • {selectedJobDetail.location}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  {/* Quick shortcut — open the "Daily Update and Photos
                      Report" dialog on the Reports page with this job
                      already selected. Skips the otherwise-required
                      "go to Reports → New Report → pick the job"
                      navigation for the most common daily action. Only
                      shown for in_progress / completed jobs (matches
                      the backend eligibility filter on the Reports
                      form) so we don't push admins into a state where
                      the job dropdown wouldn't accept it. */}
                  {(selectedJobDetail.status === 'in_progress' || selectedJobDetail.status === 'completed') && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setShowJobDetail(false);
                        navigate(`/admin/client-reports?openNewReport=1&jobId=${selectedJobDetail.id}`);
                      }}
                      data-testid="job-detail-quick-daily-update-btn"
                      className="border-purple-200 text-purple-700 hover:bg-purple-50"
                    >
                      <Plus size={14} className="mr-1" />
                      Daily Update
                    </Button>
                  )}
                  {getStatusBadge(selectedJobDetail.status)}
                </div>
              </div>

              {/* Job Stats */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-orange-50 rounded-xl p-4 text-center">
                  <Package size={24} className="mx-auto text-orange-500 mb-2" />
                  <p className="text-2xl font-bold text-gray-900">{selectedJobDetail.deliveries}</p>
                  <p className="text-xs text-gray-500">Deliveries</p>
                </div>
                {!isAuditor && (
                  <div className="bg-green-50 rounded-xl p-4 text-center">
                    <DollarSign size={24} className="mx-auto text-green-500 mb-2" />
                    <p className="text-2xl font-bold text-green-600">${selectedJobDetail.total_pay?.toFixed(2) || '0.00'}</p>
                    <p className="text-xs text-gray-500">Total Pay</p>
                  </div>
                )}
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <Calendar size={24} className="mx-auto text-blue-500 mb-2" />
                  <p className="text-sm font-bold text-gray-900">{selectedJobDetail.deadline || 'No deadline'}</p>
                  <p className="text-xs text-gray-500">Deadline</p>
                </div>
                <div className="bg-purple-50 rounded-xl p-4 text-center">
                  <Briefcase size={24} className="mx-auto text-purple-500 mb-2" />
                  <p className="text-sm font-bold text-gray-900">{selectedJobDetail.delivery_type || 'houses'}</p>
                  <p className="text-xs text-gray-500">Type</p>
                </div>
              </div>

              {/* Delivery Zone Map */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <MapPin size={18} className="text-purple-600" />
                  {(selectedJobDetail.delivery_type === 'canvassing' || selectedJobDetail.delivery_type === 'address_specific' || (selectedJobDetail.specific_addresses && selectedJobDetail.specific_addresses.length > 0)) 
                    ? 'Target Addresses' 
                    : 'Delivery Zone'}
                </h3>
                <div className="h-64 rounded-xl overflow-hidden border">
                  {/* For canvassing, address_specific, or any job with specific_addresses - show address markers */}
                  {(selectedJobDetail.delivery_type === 'canvassing' || selectedJobDetail.delivery_type === 'address_specific' || (selectedJobDetail.specific_addresses && selectedJobDetail.specific_addresses.length > 0)) && selectedJobDetail.specific_addresses && selectedJobDetail.specific_addresses.length > 0 ? (
                    (() => {
                      // Build set of delivered address IDs/indices for color coding
                      const deliveredIds = new Set(jobDeliveryHistory?.trackingProgress?.delivered_house_ids || []);
                      const defaultColor = selectedJobDetail.delivery_type === 'canvassing' ? '#4f46e5' : '#2563eb';
                      const deliveredColor = '#16a34a'; // green-600
                      return (
                        <LazyGoogleMap
                          center={
                            selectedJobDetail.specific_addresses.find(a => a.lat && a.lng) 
                              ? { lat: selectedJobDetail.specific_addresses.find(a => a.lat && a.lng).lat, lng: selectedJobDetail.specific_addresses.find(a => a.lat && a.lng).lng }
                              : selectedJobDetail.map_center 
                                ? { lat: selectedJobDetail.map_center[0], lng: selectedJobDetail.map_center[1] }
                                : { lat: 29.7604, lng: -95.3698 }
                          }
                          zoom={14}
                          markers={selectedJobDetail.specific_addresses.filter(a => a.lat && a.lng).map((addr, idx) => {
                            const addrId = addr.id || `addr_${idx}`;
                            const isDelivered = deliveredIds.has(addrId) || deliveredIds.has(String(idx));
                            return {
                              lat: addr.lat,
                              lng: addr.lng,
                              number: idx + 1,
                              label: `${addr.address}${isDelivered ? ' (Delivered)' : ''}`,
                              color: isDelivered ? deliveredColor : defaultColor
                            };
                          })}
                          rectangles={
                            // Show grid sections for parent jobs from child job boundaries
                            selectedJobDetail.is_split_parent && childProgress?.child_jobs
                              ? childProgress.child_jobs
                                  .filter(c => c.delivery_boundary && c.delivery_boundary.length >= 4)
                                  .map((c, i) => ({
                                    bounds: [[c.delivery_boundary[0][0], c.delivery_boundary[0][1]], [c.delivery_boundary[2][0], c.delivery_boundary[2][1]]],
                                    center: c.map_center ? [c.map_center[0], c.map_center[1]] : null,
                                    label: c.title?.split(' - ').pop() || `Grid ${i+1}`,
                                    count: c.deliveries || 0,
                                  }))
                              // Or show grid_sections stored on the job itself
                              : (selectedJobDetail.grid_sections && selectedJobDetail.grid_sections.length > 1)
                                ? selectedJobDetail.grid_sections.map(g => ({
                                    bounds: g.boundary ? [[g.boundary[0][0], g.boundary[0][1]], [g.boundary[2][0], g.boundary[2][1]]] : null,
                                    center: g.center,
                                    label: g.label,
                                    count: g.houseCount || g.count || 0,
                                  })).filter(r => r.bounds)
                                : []
                          }
                          polylines={jobDeliveryHistory?.sessions?.filter(s => s.route_path && s.route_path.length > 0).map(session => ({
                            path: session.route_path,
                            color: '#f97316',
                            weight: 3
                          })) || []}
                          delayLoad={150}
                        />
                      );
                    })()
                  ) : selectedJobDetail.delivery_boundary && selectedJobDetail.delivery_boundary.length > 0 ? (
                    <LazyGoogleMap
                      center={
                        selectedJobDetail.map_center 
                          ? { lat: selectedJobDetail.map_center[0], lng: selectedJobDetail.map_center[1] }
                          : { lat: 40.7128, lng: -74.0060 }
                      }
                      zoom={14}
                      polygon={selectedJobDetail.delivery_boundary}
                      polygonOptions={{ fillColor: '#8b5cf6', strokeColor: '#8b5cf6', fillOpacity: 0.2 }}
                      additionalPolygons={
                        selectedJobDetail.is_split_parent && childProgress?.child_jobs
                          ? childProgress.child_jobs
                              .filter(c => c.delivery_boundary && c.delivery_boundary.length >= 3)
                              .map((c, i) => {
                                const colors = ['#f97316', '#06b6d4', '#ec4899', '#14b8a6', '#eab308', '#6366f1', '#ef4444', '#84cc16'];
                                const color = colors[i % colors.length];
                                return {
                                  path: c.delivery_boundary,
                                  label: c.grid_label || String(i + 1),
                                  fillColor: color,
                                  fillOpacity: 0.12,
                                  strokeColor: color,
                                  strokeWeight: 2,
                                  strokeOpacity: 0.9
                                };
                              })
                          : []
                      }
                      houseLocations={jobHouseLocations}
                      polylines={jobDeliveryHistory?.sessions?.filter(s => s.route_path && s.route_path.length > 0).map(session => ({
                        path: session.route_path,
                        color: '#f97316',
                        weight: 3
                      })) || []}
                      delayLoad={150}
                    />
                  ) : (selectedJobDetail.delivery_type === 'canvassing' || selectedJobDetail.delivery_type === 'address_specific') ? (
                    <div className="h-full flex flex-col items-center justify-center bg-gray-100 p-4">
                      <MapPin size={32} className="text-gray-400 mb-2" />
                      <p className="text-gray-500 text-center">
                        {selectedJobDetail.specific_addresses?.length || 0} addresses pending geocoding
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Addresses will appear on map once geocoded
                      </p>
                    </div>
                  ) : (
                    <div className="h-full flex items-center justify-center bg-gray-100">
                      <p className="text-gray-500">No delivery zone defined</p>
                    </div>
                  )}
                </div>
                {/* Show address list for canvassing/address_specific or any job with specific_addresses */}
                {(selectedJobDetail.delivery_type === 'canvassing' || selectedJobDetail.delivery_type === 'address_specific' || (selectedJobDetail.specific_addresses && selectedJobDetail.specific_addresses.length > 0)) && selectedJobDetail.specific_addresses && selectedJobDetail.specific_addresses.length > 0 && (
                  <div className="mt-3 bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-medium text-gray-500">
                        {selectedJobDetail.specific_addresses.length} Target Addresses
                        {selectedJobDetail.specific_addresses.filter(a => a.lat && a.lng).length > 0 && (
                          <span className="text-green-600 ml-2">
                            ({selectedJobDetail.specific_addresses.filter(a => a.lat && a.lng).length} geocoded)
                          </span>
                        )}
                        {(jobDeliveryHistory?.trackingProgress?.delivered_house_ids?.length || 0) > 0 && (
                          <span className="text-emerald-600 ml-2 font-semibold">
                            · {jobDeliveryHistory.trackingProgress.delivered_house_ids.length} delivered
                          </span>
                        )}
                      </p>
                      {selectedJobDetail.specific_addresses.some(a => !a.lat || !a.lng) && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleGeocodeAddresses(selectedJobDetail.id)}
                          disabled={geocodingAddresses}
                          className="text-xs h-7 px-2"
                          data-testid="geocode-addresses-btn"
                        >
                          {geocodingAddresses ? (
                            <>
                              <Loader2 size={12} className="mr-1 animate-spin" />
                              Geocoding...
                            </>
                          ) : (
                            <>
                              <MapPin size={12} className="mr-1" />
                              Geocode All
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {(() => {
                        const deliveredIds = new Set(jobDeliveryHistory?.trackingProgress?.delivered_house_ids || []);
                        return selectedJobDetail.specific_addresses.slice(0, 20).map((addr, idx) => {
                          const addrId = addr.id || `addr_${idx}`;
                          const isDelivered = deliveredIds.has(addrId) || deliveredIds.has(String(idx));
                          return (
                            <div key={idx} className="flex items-center gap-2 text-xs">
                              <span className={`w-5 h-5 flex items-center justify-center rounded-full text-white text-[10px] ${isDelivered ? 'bg-emerald-500' : selectedJobDetail.delivery_type === 'canvassing' ? 'bg-indigo-500' : 'bg-blue-500'}`}>
                                {idx + 1}
                              </span>
                              <span className={`flex-1 ${isDelivered ? 'text-emerald-700 font-medium' : 'text-gray-700'}`}>{addr.address}</span>
                              {isDelivered ? (
                                <span className="text-emerald-600 text-[10px] font-semibold">✓ delivered</span>
                              ) : addr.lat && addr.lng ? (
                                <span className="text-green-500 text-[10px]">✓ mapped</span>
                              ) : (
                                <span className="text-amber-500 text-[10px]">pending</span>
                              )}
                            </div>
                          );
                        });
                      })()}
                      {selectedJobDetail.specific_addresses.length > 20 && (
                        <p className="text-xs text-gray-400 italic mt-2">
                          ... and {selectedJobDetail.specific_addresses.length - 20} more addresses
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Split into Grid Jobs Button - for any polygon-based job OR address_specific job */}
              {(() => {
                const canSplit = (selectedJobDetail.status === 'open' || selectedJobDetail.status === 'pending_approval' || selectedJobDetail.status === 'in_progress') &&
                  !selectedJobDetail.is_split_parent && (
                    (selectedJobDetail.delivery_boundary && selectedJobDetail.delivery_boundary.length >= 3) ||
                    ((selectedJobDetail.delivery_type === 'address_specific' || selectedJobDetail.delivery_type === 'canvassing') &&
                     selectedJobDetail.specific_addresses && 
                     selectedJobDetail.specific_addresses.filter(a => a.lat && a.lng).length >= 2)
                  );
                if (!canSplit) return null;
                const isAddrType = selectedJobDetail.delivery_type === 'address_specific' || selectedJobDetail.delivery_type === 'canvassing';
                const itemCount = isAddrType
                  ? (selectedJobDetail.specific_addresses?.filter(a => a.lat && a.lng).length || 0)
                  : selectedJobDetail.deliveries;
                const isLarge = itemCount >= 500;
                return (
                <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-indigo-900 flex items-center gap-2">
                        <Grid3X3 size={18} className="text-indigo-600" />
                        {isLarge ? 'Large' : 'Split-Ready'} {isAddrType ? 'Address List' : 'Delivery Zone'}
                      </h3>
                      <p className="text-sm text-indigo-700 mt-1">
                        This job has {itemCount} {isAddrType ? 'geocoded addresses' : 'deliveries'}.{' '}
                        {isLarge ? 'Auto-split is recommended.' : 'Split into smaller grid jobs for easier assignment.'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => handleSplitIntoGridJobs(selectedJobDetail)}
                        disabled={splittingIntoGrids}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white"
                        data-testid="split-into-grids-btn"
                      >
                        {splittingIntoGrids ? (
                          <>
                            <Loader2 size={16} className="mr-2 animate-spin" />
                            Splitting...
                          </>
                        ) : (
                          <>
                            <Grid3X3 size={16} className="mr-2" />
                            Auto-Split
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setSplitModalJob(selectedJobDetail)}
                        disabled={splittingIntoGrids}
                        className="border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                        data-testid="split-custom-btn"
                      >
                        <Settings size={16} className="mr-1" /> Custom...
                      </Button>
                    </div>
                  </div>
                </div>
                );
              })()}
              
              {/* Show message if job has been split - Parent Job Overview */}
              {selectedJobDetail.is_split_parent && (
                <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
                  <h3 className="font-semibold text-indigo-900 flex items-center gap-2 mb-2">
                    <Grid3X3 size={18} className="text-indigo-600" />
                    Parent Job — Child Jobs Overview
                  </h3>
                  <p className="text-sm text-indigo-700 mb-3">
                    This parent job has been split into child grid jobs. Progress from all child jobs is aggregated below.
                  </p>
                  
                  {childProgress ? (
                    <div className="space-y-3">
                      {/* Progress Summary */}
                      <div className="flex items-center gap-4 bg-white rounded-lg p-3 border border-indigo-100">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-indigo-700">{childProgress.child_count}</p>
                          <p className="text-xs text-gray-500">Child Jobs</p>
                        </div>
                        <div className="h-10 w-px bg-indigo-200" />
                        <div className="text-center">
                          <p className="text-2xl font-bold text-emerald-600">{childProgress.all_delivered_house_ids?.length || 0}</p>
                          <p className="text-xs text-gray-500">Houses Delivered</p>
                        </div>
                        <div className="h-10 w-px bg-indigo-200" />
                        <div className="text-center">
                          <p className="text-2xl font-bold text-blue-600">{childProgress.all_route_paths?.reduce((sum, r) => sum + r.route_path.length, 0) || 0}</p>
                          <p className="text-xs text-gray-500">GPS Points</p>
                        </div>
                      </div>
                      
                      {/* Child Job List */}
                      {childProgress.child_jobs?.length > 0 && (
                        <div className="space-y-2">
                          {childProgress.child_jobs.map(child => (
                            <div key={child.id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-indigo-100 text-sm">
                              <div className="flex items-center gap-2">
                                <Badge className={
                                  child.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                                  child.status === 'completed' ? 'bg-purple-100 text-purple-700' :
                                  child.status === 'open' ? 'bg-green-100 text-green-700' :
                                  child.status === 'partial_complete' ? 'bg-orange-100 text-orange-700' :
                                  'bg-gray-100 text-gray-700'
                                }>
                                  {child.status.replace('_', ' ')}
                                </Badge>
                                <span className="font-medium text-gray-800">{child.title}</span>
                              </div>
                              <div className="flex items-center gap-3 text-gray-500">
                                {child.worker_name && (
                                  <span className="flex items-center gap-1 text-emerald-600">
                                    <User size={12} />
                                    {child.worker_name}
                                  </span>
                                )}
                                <span>{child.delivered_count}/{child.deliveries} delivered</span>
                                {child.route_points > 0 && <span>{child.route_points} GPS pts</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-indigo-500">
                      <Loader2 size={14} className="animate-spin" />
                      Loading child job progress...
                    </div>
                  )}
                </div>
              )}
              
              {/* Team Info Section */}
              {selectedJobDetail.is_team_job && teamInfo && (
                <div className="bg-teal-50 rounded-xl p-4 border border-teal-200">
                  <h3 className="font-semibold text-teal-900 flex items-center gap-2 mb-3">
                    <Users size={18} className="text-teal-600" />
                    Team Job — {teamInfo.members?.filter(m => m.status === 'accepted').length || 0} Members
                  </h3>
                  <p className="text-sm text-teal-700 mb-3">
                    Payment is split based on each member's delivery count. Max team size: {teamInfo.max_size || 5}.
                  </p>
                  <div className="space-y-2">
                    {teamInfo.members?.map(member => (
                      <div key={member.user_id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-teal-100 text-sm">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${member.status === 'accepted' ? 'bg-emerald-500' : member.status === 'pending' ? 'bg-amber-400' : 'bg-gray-300'}`} />
                          <span className="font-medium text-gray-800">{member.name}</span>
                          <span className="text-gray-400 text-xs">{member.email}</span>
                          {member.role === 'owner' && (
                            <Badge className="bg-teal-100 text-teal-700 text-xs">Owner</Badge>
                          )}
                          {member.status === 'pending' && (
                            <Badge className="bg-amber-100 text-amber-700 text-xs">Pending</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-gray-500">
                          <span>{member.deliveries_count || 0} deliveries</span>
                          {member.gps_points > 0 && <span>{member.gps_points} GPS pts</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Re-count Houses (polygon jobs) OR Refresh Addresses (address_specific jobs or jobs with specific_addresses) */}
              {canManageJobs && (selectedJobDetail.delivery_type === 'address_specific' || selectedJobDetail.delivery_type === 'canvassing' || (selectedJobDetail.specific_addresses && selectedJobDetail.specific_addresses.length > 0)) && (
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-blue-900 flex items-center gap-2">
                        <MapPin size={18} className="text-blue-600" />
                        Refresh Addresses
                      </h3>
                      <p className="text-sm text-blue-700 mt-1">
                        {selectedJobDetail.parent_job_id
                          ? 'Re-populate geocoded addresses from parent job for this grid section'
                          : 'Re-sync address markers from this job\'s address list'}
                      </p>
                    </div>
                    <Button
                      onClick={async () => {
                        try {
                          toast.info('Refreshing addresses...', { duration: 5000 });
                          const result = await axios.post(`${API}/admin/jobs/${selectedJobDetail.id}/repopulate-addresses`, {}, getAuthHeader());
                          const count = result.data?.count || 0;
                          const geocoded = result.data?.geocoded_count || 0;
                          if (count > 0 && geocoded < count) {
                            toast.success(`Loaded ${count} addresses (${geocoded} geocoded). Use "Geocode All" to geocode remaining.`);
                          } else if (geocoded > 0) {
                            toast.success(`Refreshed ${geocoded} geocoded addresses on map`);
                          } else if (count > 0) {
                            toast.success(`Loaded ${count} addresses. Use "Geocode All" to geocode them.`);
                          } else {
                            toast.info('No addresses found. Check the parent job has addresses.');
                          }
                          // Refresh job detail to get updated specific_addresses
                          const freshJob = await axios.get(`${API}/admin/jobs/${selectedJobDetail.id}`, getAuthHeader());
                          setSelectedJobDetail(freshJob.data);
                          if (result.data?.deliveries_updated) {
                            setSelectedJobDetail(prev => ({
                              ...prev,
                              deliveries: result.data.new_deliveries,
                              total_pay: result.data.new_total_pay
                            }));
                          }
                          fetchJobs();
                        } catch (error) {
                          console.error('Error refreshing addresses:', error);
                          toast.error(error.response?.data?.detail || 'Failed to refresh addresses');
                        }
                      }}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="refresh-addresses-btn"
                    >
                      <RefreshCw size={16} className="mr-2" />
                      Refresh Addresses
                    </Button>
                  </div>
                </div>
              )}
              {canManageJobs && !['address_specific', 'canvassing'].includes(selectedJobDetail.delivery_type) && (selectedJobDetail.delivery_boundary?.length >= 3 || selectedJobDetail.delivery_polygons?.length > 0) && (
                <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-green-900 flex items-center gap-2">
                        <Home size={18} className="text-green-600" />
                        Re-count Houses
                      </h3>
                      <p className="text-sm text-green-700 mt-1">
                        Re-query parcel database + OSM for accurate house count and green dots
                      </p>
                    </div>
                    <Button
                      onClick={async () => {
                        try {
                          toast.info('Re-counting houses (Parcel DB + OSM)...', { duration: 5000 });
                          const result = await axios.post(`${API}/admin/jobs/${selectedJobDetail.id}/refresh-house-cache`, { force: true }, getAuthHeader());
                          const count = result.data?.count || 0;
                          const parcelCount = result.data?.parcel_count || 0;
                          const osmCount = result.data?.osm_count || 0;
                          const googleCount = result.data?.google_count || 0;
                          
                          // Build source info message
                          let sourceParts = [];
                          if (parcelCount > 0) sourceParts.push(`Parcel: ${parcelCount}`);
                          if (osmCount > 0) sourceParts.push(`OSM: ${osmCount}`);
                          if (googleCount > 0) sourceParts.push(`Google: ${googleCount}`);
                          const sourceInfo = sourceParts.length > 0 ? ` (${sourceParts.join(', ')})` : '';
                          
                          // Check if deliveries were updated
                          if (result.data?.deliveries_updated) {
                            const oldCount = result.data.old_deliveries;
                            const newCount = result.data.new_deliveries;
                            const oldPay = result.data.old_total_pay;
                            const newPay = result.data.new_total_pay;
                            toast.success(`Found ${count} houses${sourceInfo}! Deliveries: ${oldCount} → ${newCount}, Pay: $${oldPay?.toFixed(2) || '0.00'} → $${newPay?.toFixed(2) || '0.00'}`, { duration: 6000 });
                          } else {
                            toast.success(`Found ${count} houses${sourceInfo}! House count matches.`);
                          }
                          
                          // Always update the job details header with the re-counted values
                          setSelectedJobDetail(prev => ({
                            ...prev,
                            deliveries: result.data?.new_deliveries ?? count,
                            total_pay: result.data?.new_total_pay ?? prev.total_pay
                          }));
                          // Also refresh the jobs list
                          fetchJobs();
                          
                          // Refresh house locations on the map
                          try {
                            const houseResponse = await axios.get(`${API}/jobs/${selectedJobDetail.id}/house-locations`, getAuthHeader());
                            if (houseResponse.data?.houses?.length > 0) {
                              setJobHouseLocations(houseResponse.data.houses);
                            }
                          } catch { /* ignore */ }
                        } catch (error) {
                          console.error('Error re-counting houses:', error);
                          toast.error('Failed to re-count houses');
                        }
                      }}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid="recount-houses-btn"
                    >
                      <RefreshCw size={16} className="mr-2" />
                      Re-count Houses
                    </Button>
                    {(selectedJobDetail.delivery_boundary?.length >= 3 || selectedJobDetail.delivery_polygons?.length > 0) && (
                      <Button
                        onClick={async () => {
                          const ok = window.confirm(
                            'Scrub admin / manual marks that fall OUTSIDE this job\'s delivery zone?\n\n' +
                            'A snapshot is taken first so the cleanup is reversible. Use this if you see stray dots from a different job on the map.'
                          );
                          if (!ok) return;
                          try {
                            const res = await axios.post(
                              `${API}/admin/jobs/${selectedJobDetail.id}/mark/purge-outside-zone`,
                              {},
                              getAuthHeader()
                            );
                            const removed = res.data?.removed_count ?? 0;
                            const affected = res.data?.affected_users ?? 0;
                            if (removed === 0) {
                              toast.info('No out-of-zone marks found on this job.');
                            } else {
                              toast.success(
                                `Removed ${removed} out-of-zone mark${removed === 1 ? '' : 's'} ` +
                                `across ${affected} ${affected === 1 ? 'worker' : 'workers'}. Snapshots saved.`
                              );
                              fetchJobs();
                            }
                          } catch (e) {
                            console.error('Purge failed:', e);
                            toast.error(e.response?.data?.detail || 'Failed to purge out-of-zone marks');
                          }
                        }}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                        data-testid="purge-out-of-zone-marks-btn"
                        title="Remove stray admin/manual marks that fall outside this job's delivery zone"
                      >
                        <AlertTriangle size={16} className="mr-2" />
                        Scrub Stray Marks
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Assigned User */}
              {jobDeliveryHistory?.assignedUser && (
                <div className="bg-blue-50 rounded-xl p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <User size={18} className="text-blue-600" />
                    Assigned Worker
                  </h3>
                  <div className="flex items-center gap-4">
                    {jobDeliveryHistory.assignedUser.selfie_url ? (
                      <img 
                        src={jobDeliveryHistory.assignedUser.selfie_url}
                        alt="Worker" 
                        className="w-16 h-16 rounded-full object-cover border-2 border-white shadow"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center">
                        <User size={24} className="text-gray-400" />
                      </div>
                    )}
                    <div>
                      <p className="font-semibold text-gray-900">{jobDeliveryHistory.assignedUser.name}</p>
                      <p className="text-sm text-gray-600">{jobDeliveryHistory.assignedUser.email}</p>
                      <p className="text-sm text-gray-500">{jobDeliveryHistory.assignedUser.phone}</p>
                    </div>
                    <div className="ml-auto text-right">
                      <p className="text-sm text-gray-500">Jobs Completed</p>
                      <p className="text-xl font-bold text-blue-600">{jobDeliveryHistory.assignedUser.jobs_completed || 0}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Delivery History / Sessions */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Route size={18} className="text-orange-600" />
                    Delivery Progress
                  </h3>
                  {!isAuditor && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenEditProgress(selectedJobDetail)}
                    className="text-blue-600 border-blue-300 hover:bg-blue-50"
                    data-testid="edit-progress-btn"
                  >
                    <Edit size={14} className="mr-1" />
                    Edit Progress
                  </Button>
                  )}
                </div>
                {jobDeliveryHistory?.sessions && jobDeliveryHistory.sessions.length > 0 ? (
                  <div className="space-y-4">
                    {jobDeliveryHistory.sessions.map((session, idx) => (
                      <div key={session.id || idx} className={`rounded-xl p-4 ${session.is_tracking_progress ? (session.is_live ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-gray-200') : 'bg-gray-50'}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            {session.is_tracking_progress ? (
                              <>
                                <MapPin size={18} className={session.is_live ? 'text-blue-500' : 'text-gray-400'} />
                                <span className="font-medium">{session.is_live ? 'Live Tracking' : 'Last Tracked'}</span>
                                {session.count_from_tracking && (
                                  <span className="text-xs text-gray-400">(from GPS data)</span>
                                )}
                              </>
                            ) : (
                              <>
                                <CheckCircle size={18} className="text-green-500" />
                                <span className="font-medium">Session #{idx + 1}</span>
                                {session.count_from_tracking && (
                                  <span className="text-xs text-gray-400">(count from GPS data)</span>
                                )}
                              </>
                            )}
                          </div>
                          <Badge className={
                            session.status === 'paid' ? 'bg-green-100 text-green-700' :
                            session.status === 'approved' ? 'bg-blue-100 text-blue-700' :
                            session.status === 'in_progress' ? 'bg-orange-100 text-orange-700' :
                            (session.is_tracking_progress && session.is_live) ? 'bg-blue-100 text-blue-700' :
                            (session.is_tracking_progress && !session.is_live) ? 'bg-gray-100 text-gray-600' :
                            'bg-yellow-100 text-yellow-700'
                          }>
                            {session.is_tracking_progress ? (session.is_live ? 'Live' : 'Offline') : session.status}
                          </Badge>
                        </div>
                        
                        <div className="grid grid-cols-4 gap-3 text-sm mb-3">
                          <div>
                            <p className="text-gray-500">Deliveries</p>
                            <p className="font-semibold">{session.deliveries_count}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Distance</p>
                            <p className="font-semibold">{session.distance?.toFixed(2) || 0} mi</p>
                          </div>
                          <div>
                            <p className="text-gray-500">{session.is_tracking_progress ? 'GPS Points' : 'Earnings'}</p>
                            <p className="font-semibold text-green-600">
                              {session.is_tracking_progress 
                                ? (session.route_points || session.gps_route_points || 0)
                                : (isAuditor ? '—' : `$${session.earnings?.toFixed(2) || '0.00'}`)
                              }
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500">{session.is_tracking_progress ? 'Last Update' : 'Date'}</p>
                            <p className="font-semibold">
                              {session.created_at ? new Date(session.created_at).toLocaleString() : '-'}
                            </p>
                          </div>
                        </div>

                        {/* Admin Update Info */}
                        {session.admin_updated && (
                          <div className="bg-orange-50 border border-orange-200 rounded-lg p-2 mb-3">
                            <p className="text-xs text-orange-700 flex items-center gap-1">
                              <AlertTriangle size={12} />
                              <span className="font-medium">Manually updated by admin</span>
                              {session.admin_notes && <span>- {session.admin_notes}</span>}
                            </p>
                          </div>
                        )}

                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-xl p-8 text-center">
                    <Clock size={32} className="mx-auto text-gray-300 mb-2" />
                    <p className="text-gray-500">No delivery sessions yet</p>
                    <p className="text-sm text-gray-400">Progress will appear here once the worker starts delivering</p>
                  </div>
                )}
              </div>

              {/* Delivery Photos Gallery */}
              {(() => {
                // Use jobPhotos (fetched by job_id) as primary, fall back to session-based photos
                const directPhotos = (jobDeliveryHistory?.jobPhotos || []).map((photo, idx) => ({
                  ...photo,
                  sessionIndex: idx + 1
                }));
                const sessionPhotos = (jobDeliveryHistory?.sessions || []).flatMap((session, sIdx) =>
                  (session.photos || []).map(photo => ({
                    ...photo,
                    sessionIndex: sIdx + 1,
                    sessionId: session.id
                  }))
                );
                // Use direct photos if available, otherwise fall back to session photos
                const allPhotos = directPhotos.length > 0 ? directPhotos : sessionPhotos;
                return (
                  <div data-testid="delivery-photos-section">
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Image size={18} className="text-indigo-600" />
                      Delivery Photos
                      {allPhotos.length > 0 && (
                        <span className="text-sm font-normal text-gray-500">({allPhotos.length})</span>
                      )}
                    </h3>
                    {allPhotos.length > 0 ? (
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3" data-testid="delivery-photos-grid">
                        {allPhotos.map((photo, idx) => (
                          <div
                            key={photo.id || idx}
                            className="relative group cursor-pointer rounded-lg overflow-hidden border border-gray-200 hover:border-indigo-400 transition-colors"
                            onClick={() => setEnlargedPhoto(photo)}
                            data-testid={`delivery-photo-${idx}`}
                          >
                            <img
                              src={photo.url}
                              alt={`Delivery photo ${idx + 1}`}
                              className="w-full h-auto min-h-[100px] object-contain bg-gray-50"
                              loading="lazy"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                              <Eye size={20} className="text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow" />
                            </div>
                            <span className="absolute bottom-1 right-1 text-[9px] bg-black/50 text-white px-1 rounded">
                              #{photo.sessionIndex}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-gray-50 rounded-xl p-8 text-center" data-testid="no-photos-message">
                        <Image size={32} className="mx-auto text-gray-300 mb-2" />
                        <p className="text-gray-500">No photos uploaded</p>
                        <p className="text-sm text-gray-400">Delivery photos will appear here once the worker uploads them</p>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Close Button */}
              <div className="flex justify-end pt-4 border-t">
                <Button variant="outline" onClick={() => setShowJobDetail(false)} data-testid="close-job-detail-btn">
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Photo Lightbox */}
      {enlargedPhoto && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setEnlargedPhoto(null)}
          data-testid="photo-lightbox"
        >
          <div className="relative max-w-4xl max-h-[90vh] w-full" onClick={(e) => e.stopPropagation()}>
            <button
              className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
              onClick={() => setEnlargedPhoto(null)}
              data-testid="close-lightbox-btn"
            >
              <X size={28} />
            </button>
            <img
              src={enlargedPhoto.url}
              alt="Delivery photo enlarged"
              className="w-full h-auto max-h-[85vh] object-contain rounded-lg"
              data-testid="lightbox-image"
            />
            <div className="mt-2 flex items-center justify-between text-white/80 text-sm">
              <span>Session #{enlargedPhoto.sessionIndex}</span>
              {enlargedPhoto.created_at && (
                <span>{new Date(enlargedPhoto.created_at).toLocaleString()}</span>
              )}
              <a
                href={enlargedPhoto.url}
                download
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-white hover:text-indigo-300 transition-colors"
                data-testid="download-photo-btn"
              >
                <Download size={16} />
                Download
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Job Approval Modal */}
      {/* Custom Split modal — choose how many grid sections to split a job into */}
      <Dialog open={!!splitModalJob} onOpenChange={(open) => { if (!open) setSplitModalJob(null); }}>
        <DialogContent className="max-w-md" data-testid="split-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Grid3X3 size={20} className="text-indigo-600" />
              Split into Grid Sections
            </DialogTitle>
          </DialogHeader>
          {splitModalJob && (
            <div className="space-y-4">
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-sm">
                <p className="font-semibold text-indigo-900 truncate">{splitModalJob.title}</p>
                <p className="text-xs text-indigo-700 mt-1">
                  {splitModalJob.deliveries?.toLocaleString() || 0} deliveries ·{' '}
                  {splitModalJob.delivery_type === 'address_specific' || splitModalJob.delivery_type === 'canvassing'
                    ? `${(splitModalJob.specific_addresses || []).filter((a) => a.lat && a.lng).length} geocoded addresses`
                    : `${splitModalJob.delivery_boundary?.length || 0}-point polygon`}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Number of grid sections</label>
                <Input
                  type="number"
                  min={2}
                  max={64}
                  step={1}
                  value={customSplitCount}
                  onChange={(e) => setCustomSplitCount(Math.max(2, Math.min(64, Number(e.target.value) || 2)))}
                  data-testid="split-custom-count-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Between 2 and 64. ~{Math.ceil((splitModalJob.deliveries || 0) / Math.max(customSplitCount, 1)).toLocaleString()} deliveries per section.
                </p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
                The parent job becomes a "split parent" and won't appear in walker job lists. Each child grid section is posted as its own job with the same pay rate.
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 mt-2">
            <Button variant="outline" onClick={() => setSplitModalJob(null)} disabled={splittingIntoGrids} data-testid="split-modal-cancel">
              Cancel
            </Button>
            <Button
              onClick={async () => {
                const job = splitModalJob;
                setSplitModalJob(null);
                await handleSplitIntoGridJobs(job, customSplitCount);
              }}
              disabled={splittingIntoGrids}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="split-modal-confirm"
            >
              {splittingIntoGrids ? <Loader2 size={16} className="mr-1 animate-spin" /> : <Grid3X3 size={16} className="mr-1" />}
              Split into {customSplitCount} sections
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showApproveModal} onOpenChange={setShowApproveModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle size={24} className="text-green-600" />
              Approve Job for Posting
            </DialogTitle>
          </DialogHeader>
          
          {jobToApprove && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900">{jobToApprove.title}</h4>
                <p className="text-sm text-gray-500">{jobToApprove.client} • {jobToApprove.location}</p>
                <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
                  <div>
                    <span className="text-gray-500">Deliveries:</span>
                    <span className="ml-1 font-medium">{jobToApprove.deliveries}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Base Pay:</span>
                    <span className="ml-1 font-medium text-green-600">${jobToApprove.total_pay?.toFixed(2) || '0.00'}</span>
                  </div>
                </div>
                {jobToApprove.parent_job_id && (
                  <p className="text-xs text-orange-600 mt-2 bg-orange-50 px-2 py-1 rounded">
                    This job was created from an incomplete delivery
                  </p>
                )}
              </div>
              
              <div className="border border-yellow-300 bg-yellow-50 rounded-lg p-4">
                <label className="block text-sm font-medium text-yellow-800 mb-2 flex items-center gap-1">
                  <span className="text-yellow-600">★</span>
                  Add Bonus Pay (Optional)
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={approvalBonusPay}
                    onChange={(e) => setApprovalBonusPay(parseFloat(e.target.value) || 0)}
                    className="border-yellow-300 focus:border-yellow-500"
                    placeholder="0.00"
                    data-testid="approval-bonus-input"
                  />
                </div>
                <p className="text-xs text-yellow-700 mt-2">
                  Add bonus to make this job more attractive to workers
                </p>
              </div>
              
              {approvalBonusPay > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between">
                  <span className="text-sm font-medium text-green-800">Total Worker Pay:</span>
                  <span className="text-lg font-bold text-green-600">
                    ${((jobToApprove.total_pay || 0) + approvalBonusPay).toFixed(2)}
                  </span>
                </div>
              )}
              
              <div className="flex gap-3 pt-2">
                <Button
                  onClick={handleApproveJob}
                  disabled={approvingJob}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  data-testid="confirm-approve-btn"
                >
                  {approvingJob ? (
                    <Loader2 size={16} className="mr-2 animate-spin" />
                  ) : (
                    <CheckCircle size={16} className="mr-2" />
                  )}
                  Approve & Post Job
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowApproveModal(false);
                    setJobToApprove(null);
                    setApprovalBonusPay(0);
                  }}
                  disabled={approvingJob}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Progress Modal */}
      <Dialog open={showEditProgressModal} onOpenChange={setShowEditProgressModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit size={20} className="text-blue-600" />
              Edit Delivery Progress
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Current Progress Info */}
            {progressDetails && (
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <h4 className="font-medium text-gray-900 text-sm">Current Data Sources:</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Required:</span>
                    <span className="ml-1 font-medium">{progressDetails.total_required} deliveries</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Sessions:</span>
                    <span className="ml-1 font-medium">{progressDetails.sessions?.total_deliveries || 0}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">GPS Points:</span>
                    <span className="ml-1 font-medium">{progressDetails.tracking_progress?.route_points || 0}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Live Tracking:</span>
                    <span className="ml-1 font-medium">{progressDetails.live_tracking?.active ? 'Active' : 'Inactive'}</span>
                  </div>
                </div>
                {progressDetails.tracking_progress?.admin_updated && (
                  <p className="text-xs text-orange-600 mt-2 flex items-center gap-1">
                    <AlertTriangle size={12} />
                    Previously updated by admin
                  </p>
                )}
              </div>
            )}
            
            {/* Edit Form */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="deliveries_count" className="text-sm font-medium">
                  Deliveries Completed
                </Label>
                <Input
                  id="deliveries_count"
                  type="number"
                  min="0"
                  value={editProgressData.deliveries_count}
                  onChange={(e) => setEditProgressData(prev => ({
                    ...prev,
                    deliveries_count: parseInt(e.target.value) || 0
                  }))}
                  className="mt-1"
                  data-testid="edit-deliveries-input"
                />
              </div>
              
              <div>
                <Label htmlFor="distance" className="text-sm font-medium">
                  Distance Traveled (miles)
                </Label>
                <Input
                  id="distance"
                  type="number"
                  step="0.01"
                  min="0"
                  value={editProgressData.distance}
                  onChange={(e) => setEditProgressData(prev => ({
                    ...prev,
                    distance: parseFloat(e.target.value) || 0
                  }))}
                  className="mt-1"
                  data-testid="edit-distance-input"
                />
              </div>
              
              <div>
                <Label htmlFor="notes" className="text-sm font-medium">
                  Admin Notes (optional)
                </Label>
                <Input
                  id="notes"
                  type="text"
                  value={editProgressData.notes}
                  onChange={(e) => setEditProgressData(prev => ({
                    ...prev,
                    notes: e.target.value
                  }))}
                  placeholder="Reason for manual update..."
                  className="mt-1"
                  data-testid="edit-notes-input"
                />
              </div>
            </div>
            
            {/* Warning */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-xs text-yellow-800 flex items-start gap-2">
                <AlertTriangle size={14} className="text-yellow-600 flex-shrink-0 mt-0.5" />
                <span>
                  Manual updates are logged for audit purposes. This will override the user's tracked progress. 
                  Use this only when data didn't sync properly.
                </span>
              </p>
            </div>
            
            {/* Buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                onClick={handleSaveProgress}
                disabled={savingProgress}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                data-testid="save-progress-btn"
              >
                {savingProgress ? (
                  <Loader2 size={16} className="mr-2 animate-spin" />
                ) : (
                  <CheckCircle size={16} className="mr-2" />
                )}
                Save Progress
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowEditProgressModal(false)}
                disabled={savingProgress}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign User to Job Modal */}
      <Dialog open={showAssignUserModal} onOpenChange={(open) => { if (!assigningUser) setShowAssignUserModal(open); }}>
        <DialogContent className="sm:max-w-md" data-testid="assign-user-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <UserPlus size={20} />
              {assignUserJob?.assigned_user_id ? 'Re-assign User to Job' : 'Assign User to Job'}
            </DialogTitle>
          </DialogHeader>
          {assignUserJob && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm font-medium text-gray-900">{assignUserJob.title}</p>
                <p className="text-xs text-gray-500">Status will be set to <span className="font-semibold text-blue-600">In Progress</span></p>
                {assignUserJob.assigned_user_id && (
                  <div className="mt-2 px-2 py-1.5 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900" data-testid="reassign-banner">
                    <strong>Re-assigning from:</strong>{' '}
                    {assignUserJob.assigned_user_name || assignUserJob.assigned_user_id}
                    <p className="text-amber-700 mt-0.5">The previous worker reference will be replaced. The reassignment is recorded in the audit log.</p>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Select User</Label>
                <Input
                  placeholder="Search by name or email..."
                  value={assignUserSearch}
                  onChange={(e) => setAssignUserSearch(e.target.value)}
                  data-testid="assign-user-search"
                  className="mb-2"
                />
                <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
                  {loadingUsers ? (
                    <div className="p-4 text-center text-sm text-gray-500"><Loader2 size={16} className="inline animate-spin mr-2" />Loading users...</div>
                  ) : (
                    assignUserList
                      .filter(u => {
                        const q = assignUserSearch.toLowerCase();
                        return !q || (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
                      })
                      .map(u => (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => setAssignSelectedUserId(u.id)}
                          className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center justify-between ${assignSelectedUserId === u.id ? 'bg-blue-50 border-l-2 border-blue-500' : 'hover:bg-gray-50'}`}
                          data-testid={`assign-user-option-${u.id}`}
                        >
                          <div>
                            <span className="font-medium text-gray-900">{u.name}</span>
                            <span className="text-gray-400 ml-2 text-xs">{u.email}</span>
                          </div>
                          {assignSelectedUserId === u.id && <CheckCircle size={16} className="text-blue-600 shrink-0" />}
                        </button>
                      ))
                  )}
                  {!loadingUsers && assignUserList.filter(u => {
                    const q = assignUserSearch.toLowerCase();
                    return !q || (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
                  }).length === 0 && (
                    <div className="p-4 text-center text-sm text-gray-400">No users found</div>
                  )}
                </div>
              </div>

              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" size="sm" onClick={() => setShowAssignUserModal(false)} disabled={assigningUser} data-testid="assign-user-cancel-btn">Cancel</Button>
                <Button
                  size="sm"
                  onClick={handleAssignUser}
                  disabled={assigningUser || !assignSelectedUserId}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="assign-user-confirm-btn"
                >
                  {assigningUser ? <Loader2 size={14} className="mr-1 animate-spin" /> : <UserPlus size={14} className="mr-1" />}
                  Assign User
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Remove User from Job Modal */}
      <Dialog open={showRemoveUserModal} onOpenChange={(open) => { if (!removingUser) setShowRemoveUserModal(open); }}>
        <DialogContent className="sm:max-w-md" data-testid="remove-user-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <UserX size={20} />
              Remove User from Job
            </DialogTitle>
          </DialogHeader>
          {removeUserJob && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                <p className="text-sm font-medium text-gray-900">{removeUserJob.title}</p>
                <p className="text-xs text-gray-500">Assigned to: <span className="font-semibold text-rose-600">{removeUserJob.assigned_user_name || 'Unknown'}</span></p>
                {removeUserJob.is_team_job && (
                  <p className="text-xs text-amber-600 font-medium mt-1">This is a team job — the team will be disbanded.</p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">After removal, set job status to:</Label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setRemoveUserPostStatus('open')}
                    className={`flex-1 p-3 rounded-lg border-2 text-sm font-medium transition-colors ${removeUserPostStatus === 'open' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}
                    data-testid="remove-user-status-open"
                  >
                    Open
                    <p className="text-xs font-normal mt-0.5 opacity-75">Any user can accept</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setRemoveUserPostStatus('assigned')}
                    className={`flex-1 p-3 rounded-lg border-2 text-sm font-medium transition-colors ${removeUserPostStatus === 'assigned' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}
                    data-testid="remove-user-status-assigned"
                  >
                    Assigned
                    <p className="text-xs font-normal mt-0.5 opacity-75">Admin will reassign</p>
                  </button>
                </div>
              </div>

              <p className="text-xs text-gray-500 bg-green-50 border border-green-200 rounded-md p-2">All progress data (GPS routes, delivered houses) will be preserved.</p>

              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" size="sm" onClick={() => setShowRemoveUserModal(false)} disabled={removingUser} data-testid="remove-user-cancel-btn">Cancel</Button>
                <Button
                  size="sm"
                  onClick={handleRemoveUser}
                  disabled={removingUser}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  data-testid="remove-user-confirm-btn"
                >
                  {removingUser ? <Loader2 size={14} className="mr-1 animate-spin" /> : <UserX size={14} className="mr-1" />}
                  Remove User
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Team Management Modal */}
      <Dialog open={showTeamModal} onOpenChange={setShowTeamModal}>
        <DialogContent className="sm:max-w-lg" data-testid="team-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users size={20} className="text-teal-600" />
              {teamModalData ? 'Manage Team' : 'Form Team'}
            </DialogTitle>
          </DialogHeader>
          
          {teamModalLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 size={24} className="animate-spin text-teal-600" />
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">
                <span className="font-medium">{teamModalJob?.title}</span> — Assigned to{' '}
                <span className="font-medium text-teal-700">{teamModalJob?.assigned_user_name || 'Unknown'}</span>
              </p>
              
              {/* Current Team Members */}
              {teamModalData && teamModalData.members?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Current Team ({teamModalData.members.length}/{teamModalData.max_size})</h4>
                  <div className="space-y-2">
                    {teamModalData.members.map(m => (
                      <div key={m.user_id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 text-sm">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${m.status === 'accepted' ? 'bg-emerald-500' : 'bg-amber-400'}`} />
                          <span className="font-medium">{m.name}</span>
                          <span className="text-gray-400 text-xs">{m.email}</span>
                          {m.role === 'owner' && <Badge className="bg-teal-100 text-teal-700 text-xs">Owner</Badge>}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500 text-xs">{m.deliveries_count || 0} deliveries</span>
                          {m.role !== 'owner' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveTeamMember(m.user_id, m.name)}
                              className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                              data-testid={`remove-member-${m.user_id}`}
                            >
                              <X size={14} />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Add Members Section */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Add Members</h4>
                <Input
                  placeholder="Search users by name or email..."
                  value={teamUserSearch}
                  onChange={(e) => setTeamUserSearch(e.target.value)}
                  className="mb-2"
                  data-testid="team-user-search"
                />
                <div className="max-h-48 overflow-y-auto space-y-1 border rounded-lg p-1">
                  {teamAvailableUsers
                    .filter(u => {
                      const q = teamUserSearch.toLowerCase();
                      if (q && !u.name?.toLowerCase().includes(q) && !u.email?.toLowerCase().includes(q)) return false;
                      const existingIds = (teamModalData?.members || []).map(m => m.user_id);
                      return !existingIds.includes(u.id);
                    })
                    .slice(0, 20)
                    .map(user => {
                      const selected = teamSelectedUsers.includes(user.id);
                      return (
                        <button
                          key={user.id}
                          onClick={() => setTeamSelectedUsers(prev => selected ? prev.filter(id => id !== user.id) : [...prev, user.id])}
                          className={`w-full flex items-center justify-between px-3 py-2 rounded text-sm text-left transition ${selected ? 'bg-teal-50 border border-teal-300' : 'hover:bg-gray-50'}`}
                          data-testid={`team-user-${user.id}`}
                        >
                          <div>
                            <span className="font-medium">{user.name}</span>
                            <span className="text-gray-400 ml-2 text-xs">{user.email}</span>
                          </div>
                          {selected && <CheckCircle size={16} className="text-teal-600" />}
                        </button>
                      );
                    })}
                  {teamAvailableUsers.filter(u => {
                    const q = teamUserSearch.toLowerCase();
                    if (q && !u.name?.toLowerCase().includes(q) && !u.email?.toLowerCase().includes(q)) return false;
                    const existingIds = (teamModalData?.members || []).map(m => m.user_id);
                    return !existingIds.includes(u.id);
                  }).length === 0 && (
                    <p className="text-sm text-gray-400 text-center py-4">No available users found</p>
                  )}
                </div>
                {teamSelectedUsers.length > 0 && (
                  <Button
                    onClick={handleAddTeamMembers}
                    className="w-full mt-2 bg-teal-600 hover:bg-teal-700 text-white"
                    data-testid="add-team-members-btn"
                  >
                    <Users size={16} className="mr-2" />
                    Add {teamSelectedUsers.length} Member{teamSelectedUsers.length > 1 ? 's' : ''} to Team
                  </Button>
                )}
              </div>
              
              {/* Disband Team */}
              {teamModalData && (
                <Button
                  variant="outline"
                  onClick={handleDisbandTeam}
                  className="w-full text-red-600 border-red-300 hover:bg-red-50"
                  data-testid="disband-team-btn"
                >
                  <Trash2 size={16} className="mr-2" />
                  Disband Team
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Message-a-Worker dialog — opens when an admin clicks a worker
          name in the bold workers row on a job card. Sends via the
          existing support-chat reply endpoint so the user receives it
          in their in-app Messages inbox + unread badge updates. */}
      <Dialog
        open={!!messageWorker}
        onOpenChange={(open) => {
          if (!sendingMessage && !open) {
            setMessageWorker(null);
            setMessageBody('');
          }
        }}
      >
        <DialogContent data-testid="message-worker-modal">
          <DialogHeader>
            <DialogTitle>Send Message</DialogTitle>
          </DialogHeader>
          {messageWorker && (
            <div className="space-y-3">
              <div className="text-sm text-gray-600">
                <span className="font-semibold text-gray-900">To: {messageWorker.name}</span>
                {messageWorker.email && (
                  <span className="text-xs text-gray-400 ml-2">({messageWorker.email})</span>
                )}
                <div className="text-xs text-gray-500 mt-1">
                  Re: <span className="italic">{messageWorker.job_title}</span>
                </div>
              </div>
              <Textarea
                value={messageBody}
                onChange={(e) => setMessageBody(e.target.value)}
                placeholder="e.g. Quick check-in — please remember to End Session in the app before you head home tonight."
                rows={5}
                maxLength={2000}
                disabled={sendingMessage}
                data-testid="message-worker-input"
                autoFocus
              />
              <p className="text-xs text-gray-400">
                {messageBody.length}/2000 — lands in their in-app Messages inbox.
              </p>
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  disabled={sendingMessage}
                  onClick={() => { setMessageWorker(null); setMessageBody(''); }}
                  data-testid="message-worker-cancel"
                >
                  Cancel
                </Button>
                <Button
                  className="bg-purple-600 hover:bg-purple-500"
                  disabled={sendingMessage || !messageBody.trim()}
                  onClick={handleSendWorkerMessage}
                  data-testid="message-worker-send"
                >
                  {sendingMessage ? 'Sending…' : 'Send Message'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminJobs;